import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { usePassLevels } from '../../hooks/usePassLevels';
import AddPersonToRosterModal from '../modals/AddPersonToRosterModal';
import AddPersonModal from '../modals/AddPersonModal';
import AddBlockModal from '../modals/AddBlockModal';
import AddPositionModal from '../modals/AddPositionModal';
import AssignPersonModal from '../modals/AssignPersonModal';
import BlockTemplateModal from '../modals/BlockTemplateModal';
import AddTeamScheduleItemModal from '../modals/AddTeamScheduleItemModal';
import PrintScheduleModal from '../modals/PrintScheduleModal';
import SetMealTimeModal from '../modals/SetMealTimeModal';
import SendScheduleLinksModal from '../modals/SendScheduleLinksModal';
import RequestAvailabilityModal from '../modals/RequestAvailabilityModal';
import SendRequestsModal from '../modals/SendRequestsModal';
import PassLevelMultiSelect from '../shared/PassLevelMultiSelect';
import ResendPendingModal from '../modals/ResendPendingModal';
import PrintPreviewModal from '../modals/PrintPreviewModal';
import SendTeamEmailModal from '../modals/SendTeamEmailModal';
import KidsAvailabilityRequestModal from '../modals/KidsAvailabilityRequestModal';
import { KIDS_SESSIONS } from '../kids/kidsUi';
import { downloadCsv } from '../../utils/csv';
import {
  getPeople, upsertPerson, getTeamMembers, addTeamMember, removeTeamMember,
  getBlocks, upsertBlock, deleteBlock, getAssignments, upsertAssignment, deleteAssignment, reorderAssignments, reorderBlocks,
  getSchedule, sendScheduleLink, getTeamData, invalidateTeamData,
  setPersonRoles as setPersonRolesApi, saveTeamManual,
  saveTeamDocs, setRadioFlag, sendScheduleChangeEmail, setTeamMealTime,
  upsertTeamScheduleItem, deleteTeamScheduleItem, getServingAvailability,
  getKidsAvailability, getKidsSGLMap, setKidsSGL, getKidsGroups,
  invalidateCache,
} from '../../utils/api';
import { canUserSend } from '../../config';
import {
  formatTime as formatSchedTime, buildTags, groupByDay,
  isDateHeaderRow, isSL, isWindowedMeal, isMealItem, appliesToTeam, TagPill, personScheduleUrl,
} from '../../utils/scheduleFormat';

// Loom walkthrough that walks team leads through their whole setup. Paste the
// share URL here once it's recorded; until then the step shows a "coming soon"
// toast instead of opening a blank tab.
const LOOM_WALKTHROUGH_URL = 'https://www.loom.com/share/588c1c9af37443cfbb0a468f19ef4315';

// Preset block palette (mirrors .sc-b1..sc-b12 in index.css). A team item's
// linked-block bubble cycles through these so each block name gets a consistent
// pretty color.
const BLOCK_PILL_STYLES = [
  { background: '#D4E8DF', color: '#2D6B55' },
  { background: '#EDE0D4', color: '#7A5235' },
  { background: '#E2D8EC', color: '#5A3A7A' },
  { background: '#D8E6E0', color: '#2A5446' },
  { background: '#DDEAF5', color: '#1E4870' },
  { background: '#F5EDD8', color: '#6A4818' },
  { background: '#F0DDE8', color: '#6E2248' },
  { background: '#D8EAD0', color: '#2A5020' },
  { background: '#E8F0FA', color: '#1A3A6E' },
  { background: '#FAE8D0', color: '#7A3A10' },
  { background: '#F8E8FA', color: '#6A1A7A' },
  { background: '#D8F0EC', color: '#1A5048' },
];

function blockPillStyle(name) {
  const s = String(name || '');
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return BLOCK_PILL_STYLES[h % BLOCK_PILL_STYLES.length];
}


function formatTime(t) {
  if (!t) return '';
  const parts = t.split(':');
  if (parts.length < 2) return t;
  let h = parseInt(parts[0], 10);
  const m = parts[1].padStart(2, '0');
  const ampm = h >= 12 ? 'PM' : 'AM';
  h = h % 12 || 12;
  return `${h}:${m} ${ampm}`;
}

// ── Small shared components ──────────────────────────────────────────────────

function SetupBanner({ teamName, steps, onStepClick, onToggleStep }) {
  const done = steps.filter(s => s.done).length;
  const total = steps.length;
  const complete = total > 0 && done === total;
  // When everything's done the banner collapses into a compact "complete" bar;
  // when there's still work it stays expandable to show what's left.
  const [open, setOpen] = useState(false);
  const dasharray = 97.4;
  const dashoffset = dasharray - (dasharray * done / total);

  return (
    <div className={`setup-banner${complete ? ' setup-banner-complete' : ''}`}>
      <div className="setup-banner-inner" onClick={() => setOpen(v => !v)}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <div className="setup-progress-ring">
            <svg viewBox="0 0 36 36" style={{ width: 40, height: 40, transform: 'rotate(-90deg)' }}>
              <circle cx="18" cy="18" r="15.5" fill="none" stroke="rgba(255,255,255,0.25)" strokeWidth="3"/>
              <circle cx="18" cy="18" r="15.5" fill="none" stroke="white" strokeWidth="3"
                strokeDasharray={dasharray} strokeDashoffset={dashoffset} strokeLinecap="round"/>
            </svg>
            <span className="setup-ring-label">{done}/{total}</span>
          </div>
          <div>
            <div className="setup-banner-title">
              {complete ? `${teamName} Setup Complete` : `Finish setting up ${teamName}`}
            </div>
            <div className="setup-banner-sub">
              {complete
                ? 'All steps done · click to revisit your setup'
                : `${done} of ${total} steps complete · click to see what's left`}
            </div>
          </div>
        </div>
        <span className={`setup-chevron${open ? ' open' : ''}`}>▾</span>
      </div>
      {open && (
        <div className="setup-checklist">
          {steps.map((step) => (
            <div
              key={step.key}
              className={`setup-check-item${step.done ? ' done' : ' todo'}`}
              onClick={() => onStepClick && onStepClick(step)}
            >
              {/* Check circle. Auto-detected steps are locked (data says they're
                  done); the rest can be toggled by hand. */}
              <div
                className={`setup-check-icon${step.done ? ' done-icon' : ' todo-icon'}`}
                style={step.locked ? undefined : { cursor: 'pointer' }}
                title={step.locked
                  ? 'Completed automatically'
                  : (step.done ? 'Mark as not done' : 'Mark as done')}
                onClick={step.locked ? undefined : (e) => { e.stopPropagation(); onToggleStep && onToggleStep(step); }}
              >
                {step.done
                  ? <svg viewBox="0 0 14 14" fill="none"><path d="M2.5 7L5.5 10L11.5 4" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  : <svg viewBox="0 0 14 14" fill="none"><circle cx="7" cy="7" r="5" stroke="rgba(255,255,255,0.5)" strokeWidth="1.5"/></svg>
                }
              </div>
              <div className="setup-check-body">
                <div className="setup-check-title">{step.title}</div>
                <div className="setup-check-sub">{step.sub}</div>
              </div>
              {step.done
                ? <span className="setup-check-status">Done</span>
                : <span className="setup-check-action">{step.external ? 'Watch →' : 'Go →'}</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function CopyLinkBtn({ personId, name, showToast }) {
  return (
    <button className="copy-link-btn" onClick={e => {
      e.stopPropagation();
      navigator.clipboard.writeText(personScheduleUrl(personId));
      showToast('Link copied');
    }}>
      <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
        <rect x="4.5" y="4.5" width="7" height="7" rx="1" stroke="currentColor" strokeWidth="1.4"/>
        <path d="M3 8.5H2a1 1 0 01-1-1V2a1 1 0 011-1h5.5a1 1 0 011 1v1.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
      </svg>
      Copy Link
    </button>
  );
}

function SendBtn({ personId, personEmail, name, canSend, senderEmail, showToast, templateId }) {
  async function handleSend(e) {
    e.stopPropagation();
    if (!canSend) { showToast('Only @thebelonging.co staff can send schedule links.'); return; }
    try {
      showToast(`Sending schedule link to ${name}…`);
      await sendScheduleLink(personEmail, name, personId, templateId || 'schedule-link', senderEmail);
      showToast(`Schedule link sent to ${name}.`);
    } catch (err) {
      showToast('Error sending email — check the Apps Script logs.');
      console.error(err);
    }
  }

  return (
    <button
      className={`send-link-btn${!canSend ? ' btn-disabled' : ''}`}
      onClick={handleSend}
      title={canSend ? `Send schedule link to ${name}` : 'Only @thebelonging.co staff can send'}
      style={!canSend ? { opacity: 0.4, cursor: 'not-allowed' } : {}}
    >
      <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
        <path d="M1 7l11-5.5-5.5 11L5 8 1 7z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round"/>
      </svg>
    </button>
  );
}

// ── Role dropdown (custom, on-brand, multi-select) ───────────────────────────

function RoleSelect({ values, roles, onChange }) {
  const [open, setOpen] = useState(false);
  const [dropdownStyle, setDropdownStyle] = useState({});
  const ref = useRef();

  useEffect(() => {
    if (!open) return;
    function handle(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [open]);

  function handleToggle(e) {
    e.stopPropagation();
    if (!open && ref.current) {
      const rect = ref.current.getBoundingClientRect();
      // Each option: ~38px + "Clear" option. Always use top-based positioning (reliable with position:fixed).
      const estimatedHeight = (roles.length + 1) * 38 + 8;
      const spaceBelow = window.innerHeight - rect.bottom;
      const topVal = spaceBelow >= estimatedHeight
        ? rect.bottom + 4                                    // open below
        : Math.max(8, rect.top - estimatedHeight - 4);      // flip above
      setDropdownStyle({ position: 'fixed', top: topVal, left: rect.left });
    }
    setOpen(v => !v);
  }

  function toggleRole(role) {
    const current = values || [];
    const next = current.includes(role)
      ? current.filter(r => r !== role)
      : [...current, role];
    onChange(next);
  }

  const hasRoles = (values || []).length > 0;
  const displayLabel = hasRoles ? (values || []).join(', ') : '— no role —';

  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-block' }}>
      <button
        onClick={handleToggle}
        style={{
          fontFamily: 'var(--font-h)', fontSize: 12, fontWeight: 700,
          padding: '4px 8px 4px 10px', borderRadius: 20, whiteSpace: 'nowrap',
          border: `1px solid ${hasRoles ? 'var(--black)' : 'var(--border)'}`,
          background: hasRoles ? 'var(--black)' : 'var(--off)',
          color: hasRoles ? 'white' : 'var(--muted)',
          cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5,
          maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis',
        }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{displayLabel}</span>
        <svg width="8" height="8" viewBox="0 0 8 8" fill="none" style={{ flexShrink: 0 }}>
          <path d="M1.5 2.5L4 5L6.5 2.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>
      {open && (
        <div style={{
          ...dropdownStyle,
          zIndex: 500,
          background: 'var(--white)', border: '1px solid var(--border)',
          borderRadius: 'var(--radius)', boxShadow: '0 6px 18px rgba(0,0,0,0.12)',
          minWidth: 180, overflow: 'hidden',
        }}>
          {roles.map(role => {
            const checked = (values || []).includes(role);
            return (
              <div
                key={role}
                onClick={e => { e.stopPropagation(); toggleRole(role); }}
                style={{
                  padding: '9px 14px', cursor: 'pointer',
                  fontFamily: 'var(--font-h)', fontSize: 13, fontWeight: checked ? 700 : 500,
                  color: checked ? 'var(--black)' : 'var(--mid)',
                  background: checked ? 'var(--off)' : 'var(--white)',
                  display: 'flex', alignItems: 'center', gap: 8,
                  borderBottom: '1px solid var(--border)',
                }}
                onMouseEnter={e => { if (!checked) e.currentTarget.style.background = 'var(--off)'; }}
                onMouseLeave={e => { if (!checked) e.currentTarget.style.background = 'var(--white)'; }}
              >
                <span style={{
                  width: 14, height: 14, flexShrink: 0, borderRadius: 3,
                  border: `1.5px solid ${checked ? 'var(--black)' : 'var(--border)'}`,
                  background: checked ? 'var(--black)' : 'transparent',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  {checked && (
                    <svg width="9" height="9" viewBox="0 0 9 9" fill="none">
                      <path d="M1.5 4.5L3.5 6.5L7.5 2.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  )}
                </span>
                {role}
              </div>
            );
          })}
          {hasRoles && (
            <div
              onClick={e => { e.stopPropagation(); onChange([]); setOpen(false); }}
              style={{
                padding: '9px 14px', cursor: 'pointer',
                fontFamily: 'var(--font-h)', fontSize: 12, fontWeight: 600,
                color: 'var(--muted)',
                background: 'var(--white)',
                display: 'flex', alignItems: 'center', gap: 8,
              }}
              onMouseEnter={e => { e.currentTarget.style.background = 'var(--off)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'var(--white)'; }}
            >
              Clear all
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── SGL dropdown (Kids only) ─────────────────────────────────────────────────
// Marks a person as a Small Group Leader of a specific group — that link is
// what gives their personal page the group's kids, allergies, and rotation
// path. "None" clears it.

function SGLSelect({ value, groups, onChange }) {
  const [open, setOpen] = useState(false);
  const [dropdownStyle, setDropdownStyle] = useState({});
  const ref = useRef();

  useEffect(() => {
    if (!open) return;
    function handle(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [open]);

  const current = groups.find(g => g.id === value);

  function handleToggle(e) {
    e.stopPropagation();
    if (!open && ref.current) {
      const rect = ref.current.getBoundingClientRect();
      const estimatedHeight = (groups.length + 1) * 38 + 8;
      const spaceBelow = window.innerHeight - rect.bottom;
      const topVal = spaceBelow >= estimatedHeight
        ? rect.bottom + 4
        : Math.max(8, rect.top - estimatedHeight - 4);
      setDropdownStyle({ position: 'fixed', top: topVal, left: rect.left });
    }
    setOpen(v => !v);
  }

  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-block' }}>
      <button
        onClick={handleToggle}
        title="Small Group Leader of…"
        style={{
          fontFamily: 'var(--font-h)', fontSize: 12, fontWeight: 700,
          padding: '4px 8px 4px 10px', borderRadius: 20, whiteSpace: 'nowrap',
          border: `1px solid ${current ? (current.color || '#6AA292') : 'var(--border)'}`,
          background: current ? (current.color || '#6AA292') : 'var(--off)',
          color: current ? '#fff' : 'var(--muted)',
          cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5,
          maxWidth: 170, overflow: 'hidden', textOverflow: 'ellipsis',
        }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{current ? current.name : 'None'}</span>
        <svg width="8" height="8" viewBox="0 0 8 8" fill="none" style={{ flexShrink: 0 }}>
          <path d="M1.5 2.5L4 5L6.5 2.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>
      {open && (
        <div style={{
          ...dropdownStyle, zIndex: 500,
          background: 'var(--white)', border: '1px solid var(--border)',
          borderRadius: 'var(--radius)', boxShadow: '0 6px 18px rgba(0,0,0,0.12)',
          minWidth: 180, overflow: 'hidden',
        }}>
          {groups.map(g => (
            <div
              key={g.id}
              onClick={e => { e.stopPropagation(); onChange(g.id === value ? '' : g.id); setOpen(false); }}
              style={{
                padding: '9px 14px', cursor: 'pointer',
                fontFamily: 'var(--font-h)', fontSize: 13, fontWeight: g.id === value ? 700 : 500,
                color: g.id === value ? 'var(--black)' : 'var(--mid)',
                background: g.id === value ? 'var(--off)' : 'var(--white)',
                display: 'flex', alignItems: 'center', gap: 8,
                borderBottom: '1px solid var(--border)',
              }}
              onMouseEnter={e => { if (g.id !== value) e.currentTarget.style.background = 'var(--off)'; }}
              onMouseLeave={e => { if (g.id !== value) e.currentTarget.style.background = 'var(--white)'; }}
            >
              <span style={{ width: 12, height: 12, borderRadius: '50%', background: g.color || '#6AA292', flexShrink: 0, border: '1px solid var(--border)' }} />
              {g.name}
            </div>
          ))}
          {value && (
            <div
              onClick={e => { e.stopPropagation(); onChange(''); setOpen(false); }}
              style={{ padding: '9px 14px', cursor: 'pointer', fontFamily: 'var(--font-h)', fontSize: 12, fontWeight: 600, color: 'var(--muted)' }}
              onMouseEnter={e => { e.currentTarget.style.background = 'var(--off)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'var(--white)'; }}
            >
              None — not a small group leader
            </div>
          )}
          {groups.length === 0 && (
            <div style={{ padding: '9px 14px', fontFamily: 'var(--font-b)', fontSize: 12, color: 'var(--muted)' }}>
              No groups yet — add them on the Small Groups tab.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Sub-view: People ─────────────────────────────────────────────────────────

function TLPeopleView({
  people, teamName, teamId, showToast,
  openPersonPanel, canSend, senderEmail,
  setAddPersonOpen, setSendLinksOpen, setRequestAvailOpen, setSendTeamEmailOpen,
  onSendRequestsAll, teamUnsentCount,
  onRemovePerson, teamRoles, personRoles, onSetRole,
  passLevels, onSetPassLevel,
  radioFlags, onToggleRadio,
  isKids, kidsAvailMap, kidsGroups, kidsSglMap, onSetSGL,
}) {
  const [pillFilter, setPillFilter] = useState('all');
  const [roleFilter, setRoleFilter] = useState('');
  const [confirmPersonId, setConfirmPersonId] = useState(null);
  const [search, setSearch] = useState('');
  const [nameSort, setNameSort] = useState(''); // '' = roster order, 'asc' | 'desc'

  const rolesEnabled = teamRoles?.enabled && teamRoles?.roles?.length > 0;

  // People scoped to the active role filter — drives both the top stat chips and the table
  const roleScopedPeople = roleFilter === '__none'
    ? people.filter(p => !p.roles || p.roles.length === 0)
    : roleFilter
      ? people.filter(p => (p.roles || []).includes(roleFilter))
      : people;

  const dots = {
    all: roleScopedPeople.length,
    registered: roleScopedPeople.filter(p => p.registered).length,
    pending: roleScopedPeople.filter(p => !p.registered).length,
    unplaced: roleScopedPeople.filter(p => p.status === 'unplaced').length,
  };

  const filtered = roleScopedPeople.filter(p => {
    if (pillFilter === 'registered' && !p.registered) return false;
    if (pillFilter === 'pending' && p.registered) return false;
    if (pillFilter === 'unplaced' && p.status !== 'unplaced') return false;
    const q = search.trim().toLowerCase();
    if (q && !(p.name || '').toLowerCase().includes(q) && !(p.email || '').toLowerCase().includes(q)) return false;
    return true;
  });
  if (nameSort) {
    filtered.sort((a, b) => {
      const cmp = (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' });
      return nameSort === 'asc' ? cmp : -cmp;
    });
  }

  return (
    <>
      <div style={{ padding: '24px 40px 16px', background: 'var(--white)', borderBottom: '1px solid var(--border)' }}>
        <div className="page-title-row" style={{ marginBottom: 16 }}>
          <div>
            <div className="page-title">{teamName}</div>
            <div className="page-sub">2026</div>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            {isKids && (
              <button
                className="btn btn-sm"
                title="Download everyone's availability answers as a CSV"
                onClick={() => {
                  const headers = ['Name', 'Email', 'Phone', 'Submitted', 'Max Sessions',
                    ...KIDS_SESSIONS.map(s => `${s.title} (${s.day} ${s.period})`)];
                  const rows = people.map(p => {
                    const a = kidsAvailMap?.[p.id];
                    const sel = new Set(a?.selected || []);
                    return [p.name, p.email, p.phone || '', a ? 'Yes' : 'No', a ? (a.maxSessions || '') : '',
                      ...KIDS_SESSIONS.map(s => a ? (sel.has(s.key) ? 'Yes' : 'No') : '')];
                  });
                  downloadCsv('kids-availability.csv', headers, rows);
                }}
              >
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ marginRight: 5, verticalAlign: -2 }}>
                  <path d="M7 1v8M4 6l3 3 3-3M2 12.5h10" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Availability CSV
              </button>
            )}
            <button className="btn btn-sm" onClick={() => setRequestAvailOpen(true)}>
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ marginRight: 5, verticalAlign: -2 }}>
                <rect x="1.5" y="2.5" width="11" height="10" rx="1.5" stroke="currentColor" strokeWidth="1.4"/>
                <path d="M4 1v3M10 1v3M1.5 6h11" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
              </svg>
              Request Availability
            </button>
            {onSendRequestsAll && (
              <button
                className="btn btn-sm btn-amber"
                onClick={onSendRequestsAll}
                title="Send requests for every role and block on this team at once"
              >
                ✉ Send Requests{teamUnsentCount ? ` (${teamUnsentCount})` : ''}
              </button>
            )}
            <button className="btn btn-sm" onClick={() => setSendLinksOpen(true)}>
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ marginRight: 5, verticalAlign: -2 }}>
                <path d="M1 7l11-5.5-5.5 11L5 8 1 7z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round"/>
              </svg>
              Send Schedule Links
            </button>
            <button className="btn btn-sm" onClick={() => setSendTeamEmailOpen(true)}>
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ marginRight: 5, verticalAlign: -2 }}>
                <path d="M1 3.5h12v8H1z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round"/>
                <path d="M1 3.5l6 5 6-5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
              </svg>
              Email Team
            </button>
            <button className="btn btn-dark" onClick={() => setAddPersonOpen(true)}>+ Add Person</button>
          </div>
        </div>

        <div style={{ maxWidth: 360, marginBottom: 12 }}>
          <input
            className="search-bar"
            type="text"
            placeholder="Search by name or email…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {[
            { key: 'all', label: 'on roster', num: dots.all },
            { key: 'registered', label: 'registered', num: dots.registered },
            { key: 'pending', label: 'not yet registered', num: dots.pending },
            { key: 'unplaced', label: 'not yet placed', num: dots.unplaced },
          ].map(f => (
            <button
              key={f.key}
              className={`stat-chip${f.key !== 'all' ? ` ${f.key}` : ''}${pillFilter === f.key ? ' active-chip' : ''}`}
              onClick={() => setPillFilter(f.key)}
            >
              <span className="stat-chip-num">{f.num}</span> {f.label}
            </button>
          ))}
        </div>

        {rolesEnabled && (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 10, paddingTop: 10, borderTop: '1px solid var(--border)' }}>
            <span style={{ fontFamily: 'var(--font-h)', fontSize: 11, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--muted)', alignSelf: 'center', marginRight: 2 }}>Role</span>
            <button
              className={`stat-chip${!roleFilter ? ' active-chip' : ''}`}
              onClick={() => setRoleFilter('')}
            >
              All
            </button>
            {teamRoles.roles.map(role => {
              const count = people.filter(p => (p.roles || []).includes(role)).length;
              return (
                <button
                  key={role}
                  className={`stat-chip${roleFilter === role ? ' active-chip' : ''}`}
                  onClick={() => setRoleFilter(roleFilter === role ? '' : role)}
                >
                  <span className="stat-chip-num">{count}</span> {role}
                </button>
              );
            })}
            {people.filter(p => !p.roles || p.roles.length === 0).length > 0 && (
              <button
                className={`stat-chip${roleFilter === '__none' ? ' active-chip' : ''}`}
                onClick={() => setRoleFilter(roleFilter === '__none' ? '' : '__none')}
              >
                <span className="stat-chip-num">{people.filter(p => !p.roles || p.roles.length === 0).length}</span> Unassigned
              </button>
            )}
          </div>
        )}
      </div>

      <div className="page-content" style={{ paddingTop: 24 }}>
        <div className="panel">
          {people.length === 0 ? (
            <div style={{ padding: '40px 24px', textAlign: 'center', color: 'var(--muted)', fontFamily: 'var(--font-b)', fontWeight: 300 }}>
              No one on the roster yet. Click <strong style={{ fontFamily: 'var(--font-h)', color: 'var(--black)' }}>+ Add Person</strong> to add your first team member.
            </div>
          ) : (
            <div className="table-scroll">
            <table className="data-table">
              <thead>
                <tr>
                  <th
                    onClick={() => setNameSort(s => s === 'asc' ? 'desc' : s === 'desc' ? '' : 'asc')}
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                    title="Sort by name (A–Z / Z–A / roster order)"
                  >
                    Name{nameSort === 'asc' ? ' ▲' : nameSort === 'desc' ? ' ▼' : ''}
                  </th>
                  <th>Registration</th>
                  <th>Pass Level</th>
                  <th>Status</th>
                  {rolesEnabled && <th>Role</th>}
                  {isKids && <th>Availability</th>}
                  {isKids && <th>Small Group</th>}
                  <th>Schedule</th>
                  <th>Radio</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(person => (
                  <tr key={person.id} onClick={() => openPersonPanel(person)}>
                    <td>
                      <span className="td-name">{person.name}</span>
                      {isKids && person.kidsCoupon && (
                        <span title={`Used the ${person.kidsCoupon} code — can be scheduled for anything in Kids`} style={{ color: '#FFDB7C', marginLeft: 6, fontSize: 14, textShadow: '0 0 1px #b8860b' }}>★</span>
                      )}
                      <br />
                      <span style={{ fontSize: 12, color: 'var(--muted)' }}>{person.email}</span>
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                        {person.registered
                          ? <span className="pill pill-green" style={{ fontSize: 9, padding: '3px 9px' }}>Registered</span>
                          : <span className="pill pill-amber" style={{ fontSize: 9, padding: '3px 9px' }}>Not Registered</span>
                        }
                        {/clear/i.test(person.bgCheck || '') ? (
                          <span className="pill pill-green" style={{ fontSize: 9, padding: '3px 9px' }}>✓ BG</span>
                        ) : person.registered ? (
                          <span className="pill" style={{ fontSize: 9, padding: '3px 9px', background: '#fee', color: '#b00000', border: '1px solid #f5c2c2' }}>No BG Check</span>
                        ) : null}
                      </div>
                    </td>
                    <td onClick={e => e.stopPropagation()}>
                      <PassLevelMultiSelect
                        value={person.passLevel || ''}
                        options={passLevels || []}
                        onChange={v => onSetPassLevel(person.id, v)}
                      />
                    </td>
                    <td>
                      {person.status === 'accepted'      && <><span className="status-dot dot-green" />{person.statusLabel}</>}
                      {person.status === 'pending'       && <><span className="status-dot dot-amber" />{person.statusLabel}</>}
                      {person.status === 'placed-unsent' && <><span className="status-dot dot-gray"  />{person.statusLabel}</>}
                      {person.status === 'unplaced'      && <><span className="status-dot dot-gray"  />{person.statusLabel}</>}
                    </td>
                    {rolesEnabled && (
                      <td onClick={e => e.stopPropagation()}>
                        <RoleSelect
                          values={person.roles || []}
                          roles={teamRoles.roles}
                          onChange={newRoles => onSetRole(person.id, newRoles)}
                        />
                      </td>
                    )}
                    {isKids && (
                      <td style={{ whiteSpace: 'nowrap' }}>
                        {kidsAvailMap?.[person.id] ? (
                          <span style={{ fontFamily: 'var(--font-b)', fontSize: 12, color: 'var(--mid)' }}>
                            up to <strong style={{ fontFamily: 'var(--font-h)' }}>{kidsAvailMap[person.id].maxSessions || '?'}</strong>
                            {' '}· {(kidsAvailMap[person.id].selected || []).length} free
                          </span>
                        ) : (
                          <span style={{ fontSize: 12, color: 'var(--subtle)' }}>No response</span>
                        )}
                      </td>
                    )}
                    {isKids && (
                      <td onClick={e => e.stopPropagation()}>
                        <SGLSelect
                          value={kidsSglMap?.[person.id] || ''}
                          groups={kidsGroups || []}
                          onChange={gid => onSetSGL(person.id, gid)}
                        />
                      </td>
                    )}
                    <td onClick={e => e.stopPropagation()}>
                      {person.status !== 'unplaced' ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                          <CopyLinkBtn personId={person.id} name={person.name} showToast={showToast} />
                          <SendBtn personId={person.id} personEmail={person.email} name={person.name} canSend={canSend} senderEmail={senderEmail} showToast={showToast} templateId={isKids ? 'kids-schedule-link' : undefined} />
                        </div>
                      ) : (
                        <span style={{ fontSize: 12, color: 'var(--subtle)' }}>Not yet generated</span>
                      )}
                    </td>
                    <td onClick={e => e.stopPropagation()} style={{ textAlign: 'center' }}>
                      <input
                        type="checkbox"
                        checked={radioFlags?.includes(person.id) || false}
                        onChange={() => onToggleRadio(person.id)}
                        title={radioFlags?.includes(person.id) ? 'Needs radio — uncheck to remove' : 'Check to flag as needs radio'}
                        style={{ accentColor: '#202020' }}
                      />
                    </td>
                    <td onClick={e => e.stopPropagation()} style={{ position: 'relative' }}>
                      {/* The X stays put; the confirm floats over the row as a
                          popover anchored to the right edge and opening leftward,
                          so it can't be clipped by the panel's overflow:hidden. */}
                      <button
                        className="btn-remove"
                        title="Remove from roster"
                        onClick={() => setConfirmPersonId(person.id)}
                      >
                        <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                          <path d="M2 2l8 8M10 2L2 10" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
                        </svg>
                      </button>
                      {confirmPersonId === person.id && (
                        <div style={{
                          position: 'absolute', top: '50%', right: 16, transform: 'translateY(-50%)',
                          display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap',
                          background: 'var(--white)', border: '1px solid var(--border)',
                          borderRadius: 'var(--radius)', padding: '6px 10px',
                          boxShadow: '0 4px 16px rgba(0,0,0,0.14)', zIndex: 30,
                        }}>
                          <span style={{ fontFamily: 'var(--font-b)', fontSize: 12, fontWeight: 500, color: 'var(--black)' }}>Remove {person.name}?</span>
                          <button className="btn btn-xs" onClick={() => setConfirmPersonId(null)}>Cancel</button>
                          <button className="btn btn-xs btn-danger" onClick={() => { setConfirmPersonId(null); onRemovePerson(person); }}>Remove</button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

// ── Sub-view: Roster (Blocks + Positions) ────────────────────────────────────

const SEND_ICON = (
  <svg width="13" height="13" viewBox="0 0 14 14" fill="none" style={{ marginRight: 5, verticalAlign: -2 }}>
    <path d="M1 7l11-5.5-5.5 11L5 8 1 7z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round"/>
  </svg>
);

const PRINT_ICON = (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
    <rect x="2" y="5" width="10" height="7" rx="1" stroke="currentColor" strokeWidth="1.4"/>
    <path d="M4 5V3a1 1 0 011-1h4a1 1 0 011 1v2" stroke="currentColor" strokeWidth="1.4"/>
    <rect x="4" y="8" width="6" height="1" rx=".5" fill="currentColor"/>
    <rect x="4" y="10" width="4" height="1" rx=".5" fill="currentColor"/>
  </svg>
);

const PENCIL_ICON = (
  <svg width="11" height="11" viewBox="0 0 12 12" fill="none" style={{ verticalAlign: -1 }}>
    <path d="M8.5 1.5l2 2L3 11H1v-2L8.5 1.5z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round"/>
  </svg>
);

function getDayFromDate(dateStr) {
  if (!dateStr) return '';
  try { return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long' }); }
  catch { return ''; }
}

// Stable key for matching a meal's window/chosen-time to a schedule row. Trims
// and lowercases the activity so case/whitespace drift on a schedule rebuild
// doesn't drop the team's saved meal time. Mirrors _mealKey() in Code.gs.
function mealKeyOf(date, activity) {
  return `${String(date || '').trim()}|${String(activity || '').trim().toLowerCase()}`;
}

function TLRosterView({
  enrichedBlocks, teamName, activeSubRole, showToast,
  onAddBlock, onAddPosition, onAssign,
  onRemoveBlock, onRemoveAssignment, onClearPerson,
  onRenameBlock, onRenamePosition, onEditBlockDetails, onConfirmPerson,
  setBlockTemplateOpen,
  onSendRequests, onResendPending, onPrint, onReorderPositions, onReorderBlocks,
  unsentCount, pendingCount,
  kidsEnabled, onToggleKidsFlag,
  groupBlocks, staffLeads,
}) {
  // Staff/Leads all-meals pill — ONLY on blocks that were explicitly given a
  // meals end date (the per-block opt-in). Every other block is a regular
  // timed block, even on a Staff/Leads team.
  const staffRangeLabel = (block) => {
    if (!staffLeads || !block.date || !block.endDate) return '';
    const fmt = (d) => {
      try { return new Date(d + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }); }
      catch { return d; }
    };
    return `All meals · ${fmt(block.date)} – ${fmt(block.endDate)}`;
  };
  const [rosterFilter, setRosterFilter] = useState(null);
  const [confirmId, setConfirmId] = useState(null);
  // Drag-to-reorder positions (only when not filtered, so the full list reorders).
  const [dragId, setDragId] = useState(null);
  const [dragOverId, setDragOverId] = useState(null);
  // Drag-to-reorder whole blocks (grip in each block header).
  const [blockDragId, setBlockDragId] = useState(null);
  const [blockDragOverId, setBlockDragOverId] = useState(null);
  // Collapsed block cards — header stays, positions hide (long-list relief).
  const [collapsedBlocks, setCollapsedBlocks] = useState(() => new Set());

  function handlePosDrop(block, targetId) {
    const src = dragId;
    setDragId(null);
    setDragOverId(null);
    if (src == null || src === targetId) return;
    const ids = block.positions.map(p => p.id);
    const from = ids.indexOf(src);
    const to = ids.indexOf(targetId);
    if (from === -1 || to === -1) return;
    ids.splice(to, 0, ids.splice(from, 1)[0]);
    if (onReorderPositions) onReorderPositions(block.id, ids);
  }

  function handleBlockDrop(targetId) {
    const src = blockDragId;
    setBlockDragId(null);
    setBlockDragOverId(null);
    if (src == null || src === targetId) return;
    const ids = enrichedBlocks.map(b => b.id);
    const from = ids.indexOf(src);
    const to = ids.indexOf(targetId);
    if (from === -1 || to === -1) return;
    ids.splice(to, 0, ids.splice(from, 1)[0]);
    if (onReorderBlocks) onReorderBlocks(ids);
  }
  const [confirmBlockId, setConfirmBlockId] = useState(null);
  const [editingBlockId, setEditingBlockId] = useState(null);
  const [editingBlockTitle, setEditingBlockTitle] = useState('');
  const [editingPosId, setEditingPosId] = useState(null);
  const [editingPosLabel, setEditingPosLabel] = useState('');
  const [editingDetailsId, setEditingDetailsId] = useState(null);
  const [editingDetails, setEditingDetails] = useState({ day: '', date: '', startTime: '', endTime: '' });

  function startEditBlock(block) {
    setEditingBlockId(block.id);
    setEditingBlockTitle(block.title);
  }

  function commitEditBlock(blockId) {
    const trimmed = editingBlockTitle.trim();
    if (trimmed) onRenameBlock(blockId, trimmed);
    setEditingBlockId(null);
  }

  function startEditPos(pos) {
    setEditingPosId(pos.id);
    setEditingPosLabel(pos.label);
  }

  function commitEditPos(posId) {
    const trimmed = editingPosLabel.trim();
    if (trimmed) onRenamePosition(posId, trimmed);
    setEditingPosId(null);
  }

  function startEditDetails(block) {
    setEditingDetailsId(block.id);
    setEditingDetails({ day: block.day || '', date: block.date || '', endDate: block.endDate || '', startTime: block.startTime || '', endTime: block.endTime || '' });
  }

  function commitEditDetails(blockId) {
    // Same rule as block creation for EVERY team: date + start time are
    // mandatory (group-block teams never reach this editor — it's hidden for
    // them). On a Staff/Leads team the optional "all meals through" end date
    // rides along without ever touching the block's times.
    if (!editingDetails.date) { showToast('Date is required.'); return; }
    if (!editingDetails.startTime) { showToast('Start time is required.'); return; }
    if (staffLeads && editingDetails.endDate && editingDetails.endDate < editingDetails.date) {
      showToast('The meals end date must be on or after the block date.');
      return;
    }
    onEditBlockDetails(blockId, staffLeads ? editingDetails : { ...editingDetails, endDate: '' });
    setEditingDetailsId(null);
  }

  const rosterTitle = activeSubRole ? `${activeSubRole} Roster` : 'Roster';

  return (
    <>
      <div style={{ padding: '24px 40px 20px', background: 'var(--white)', borderBottom: '1px solid var(--border)' }}>
        <div className="page-title-row" style={{ marginBottom: 0 }}>
          <div>
            <div className="page-title">{rosterTitle}</div>
            <div className="page-sub">{teamName} · 2026</div>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button
              className="btn btn-sm"
              style={{ display: 'flex', alignItems: 'center', gap: 6 }}
              onClick={() => onPrint()}
              title="Print every block for a chosen day on one print-off"
            >
              {PRINT_ICON} Print Day
            </button>
            {unsentCount > 0 && (
              <button className="btn btn-sm btn-amber" onClick={onSendRequests}>
                {SEND_ICON}Send Requests
              </button>
            )}
            {pendingCount > 0 && (
              <button className="btn btn-sm" onClick={onResendPending}>
                {SEND_ICON}Resend Pending
              </button>
            )}
            <button className="btn btn-sm" onClick={() => setBlockTemplateOpen(true)}>Edit Block Templates</button>
          </div>
        </div>
      </div>

      <div className="page-content" style={{ paddingTop: 24 }}>
        {enrichedBlocks.length === 0 ? (
          <div className="panel" style={{ padding: '40px 24px', textAlign: 'center', color: 'var(--muted)', fontFamily: 'var(--font-b)', fontWeight: 300 }}>
            {activeSubRole ? (
              <>
                No blocks yet for <strong style={{ fontFamily: 'var(--font-h)', color: 'var(--black)' }}>{activeSubRole}</strong>.
                Click <strong style={{ fontFamily: 'var(--font-h)', color: 'var(--black)' }}>+ Add Block</strong> below to create your first block for this role.
              </>
            ) : (
              <>No blocks yet. Click <strong style={{ fontFamily: 'var(--font-h)', color: 'var(--black)' }}>+ Add Block</strong> below to create your first conference block.</>
            )}
          </div>
        ) : (
          <>
            <div style={{ marginBottom: 20, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <span style={{ fontFamily: 'var(--font-h)', fontSize: 11, fontWeight: 700, letterSpacing: '0.15em', textTransform: 'uppercase', color: 'var(--muted)', marginRight: 4 }}>Filter</span>
              {[
                { key: 'accepted', label: 'Accepted',  dotClass: 'bss-accepted', pillClass: 'rsp-accepted' },
                { key: 'pending',  label: 'Pending',   dotClass: 'bss-pending',  pillClass: 'rsp-pending'  },
                { key: 'declined', label: 'Declined',  dotClass: 'bss-declined', pillClass: 'rsp-declined' },
                { key: 'unsent',   label: 'Not Sent',  dotClass: 'bss-unsent',   pillClass: 'rsp-unsent'   },
              ].map(f => (
                <button
                  key={f.key}
                  className={`roster-filter-pill ${f.pillClass}${rosterFilter === f.key ? ' active' : ''}`}
                  onClick={() => setRosterFilter(rosterFilter === f.key ? null : f.key)}
                >
                  <span className={`bss-dot ${f.dotClass}`} />
                  {f.label}
                </button>
              ))}
              {rosterFilter && (
                <button className="roster-filter-pill rsp-clear" onClick={() => setRosterFilter(null)}>× Clear</button>
              )}
              <span style={{ flex: 1 }} />
              <button
                className="btn btn-xs"
                onClick={() => setCollapsedBlocks(prev =>
                  prev.size >= enrichedBlocks.length && enrichedBlocks.length > 0
                    ? new Set()
                    : new Set(enrichedBlocks.map(b => b.id))
                )}
                title="Collapse or expand every block"
              >
                {collapsedBlocks.size >= enrichedBlocks.length && enrichedBlocks.length > 0 ? '▾ Expand All' : '▸ Collapse All'}
              </button>
            </div>

            {enrichedBlocks.map(block => {
              const visiblePositions = rosterFilter
                ? block.positions.filter(p => p.open ? rosterFilter === 'unsent' : p.status === rosterFilter)
                : block.positions;

              return (
                <div
                  key={block.id}
                  className="block-card"
                  onDragOver={blockDragId != null ? (e) => { e.preventDefault(); setBlockDragOverId(block.id); } : undefined}
                  onDrop={blockDragId != null ? () => handleBlockDrop(block.id) : undefined}
                  style={{
                    ...(blockDragOverId === block.id && blockDragId != null && blockDragId !== block.id ? { borderTop: '3px solid var(--black)' } : {}),
                    ...(blockDragId === block.id ? { opacity: 0.5 } : {}),
                  }}
                >
                  <div className="block-card-header">
                    <span
                      draggable
                      onDragStart={() => setBlockDragId(block.id)}
                      onDragEnd={() => { setBlockDragId(null); setBlockDragOverId(null); }}
                      title="Drag to reorder blocks"
                      style={{ cursor: 'grab', userSelect: 'none', color: 'var(--subtle)', fontSize: 14, padding: '2px 6px 2px 0', flexShrink: 0 }}
                    >
                      ⠿
                    </span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      {editingBlockId === block.id ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <input
                            className="form-input"
                            style={{ fontFamily: 'var(--font-h)', fontSize: 15, fontWeight: 800, padding: '3px 8px', height: 'auto', maxWidth: 260 }}
                            value={editingBlockTitle}
                            autoFocus
                            onChange={e => setEditingBlockTitle(e.target.value)}
                            onKeyDown={e => { if (e.key === 'Enter') commitEditBlock(block.id); if (e.key === 'Escape') setEditingBlockId(null); }}
                          />
                          <button className="btn btn-xs btn-dark" onClick={() => commitEditBlock(block.id)}>Save</button>
                          <button className="btn btn-xs" onClick={() => setEditingBlockId(null)}>Cancel</button>
                        </div>
                      ) : (
                        <div className="block-card-title" style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                          {block.title}
                          <button
                            className="btn-ghost"
                            style={{ opacity: 0.4, padding: '2px 4px', color: 'var(--black)' }}
                            title="Rename block"
                            onClick={() => startEditBlock(block)}
                          >
                            {PENCIL_ICON}
                          </button>
                        </div>
                      )}
                      {editingDetailsId === block.id ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
                          <input
                            className="form-input"
                            type="date"
                            value={editingDetails.date}
                            title="Block date"
                            onChange={e => {
                              const d = e.target.value;
                              setEditingDetails(prev => ({ ...prev, date: d, day: getDayFromDate(d) }));
                            }}
                            style={{ width: 150, fontSize: 12, padding: '4px 8px', height: 'auto' }}
                          />
                          <input
                            className="form-input"
                            type="time"
                            value={editingDetails.startTime}
                            onChange={e => setEditingDetails(d => ({ ...d, startTime: e.target.value }))}
                            style={{ width: 110, fontSize: 12, padding: '4px 8px', height: 'auto' }}
                          />
                          <span style={{ color: 'var(--muted)', fontSize: 12 }}>–</span>
                          <input
                            className="form-input"
                            type="time"
                            value={editingDetails.endTime}
                            onChange={e => setEditingDetails(d => ({ ...d, endTime: e.target.value }))}
                            style={{ width: 110, fontSize: 12, padding: '4px 8px', height: 'auto' }}
                          />
                          {staffLeads && (
                            <>
                              <span style={{ color: 'var(--muted)', fontSize: 12, whiteSpace: 'nowrap' }}>🍽 All meals through</span>
                              <input
                                className="form-input"
                                type="date"
                                value={editingDetails.endDate}
                                title="Optional: everyone on this block gets every meal from the block date through this date. Leave blank for normal meal rules."
                                onChange={e => setEditingDetails(d => ({ ...d, endDate: e.target.value }))}
                                style={{ width: 150, fontSize: 12, padding: '4px 8px', height: 'auto' }}
                              />
                            </>
                          )}
                          <button className="btn btn-xs btn-dark" onClick={() => commitEditDetails(block.id)}>Save</button>
                          <button className="btn btn-xs" onClick={() => setEditingDetailsId(null)}>Cancel</button>
                        </div>
                      ) : (
                        <div className="block-card-meta" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <span>{[groupBlocks ? 'Group' : block.meta, block.positionsMeta].filter(Boolean).join(' · ')}</span>
                          {staffRangeLabel(block) && (
                            <span
                              className="sc-pill"
                              style={{ background: '#F2DADA', color: '#8B3A3A', flexShrink: 0 }}
                              title="Everyone assigned to this block gets every meal between these dates"
                            >
                              🍽 {staffRangeLabel(block)}
                            </span>
                          )}
                          {!groupBlocks && (
                            <button
                              className="btn-ghost"
                              style={{ opacity: 0.35, padding: '1px 3px', color: 'var(--black)', flexShrink: 0 }}
                              title="Edit block day / date / time"
                              onClick={() => startEditDetails(block)}
                            >
                              {PENCIL_ICON}
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button
                        className="btn btn-xs"
                        onClick={() => setCollapsedBlocks(prev => {
                          const next = new Set(prev);
                          if (next.has(block.id)) next.delete(block.id); else next.add(block.id);
                          return next;
                        })}
                        title={collapsedBlocks.has(block.id) ? 'Expand block' : 'Collapse block'}
                      >
                        {collapsedBlocks.has(block.id) ? '▸' : '▾'}
                      </button>
                      <button
                        className="btn btn-xs"
                        onClick={() => onAddPosition(block.id)}
                      >
                        + Position
                      </button>
                      {confirmBlockId === block.id ? (() => {
                        // Spell out the stakes: deleting a block PERMANENTLY deletes
                        // every position in it — including filled ones. A whole
                        // Youth block's worth of scheduling died to a generic
                        // confirm once; never again.
                        const posCount = (block.positions || []).length;
                        const filledCount = (block.positions || []).filter(p => p.personId).length;
                        return (
                          <>
                            <span style={{ fontFamily: 'var(--font-b)', fontSize: 12, fontWeight: 500, color: filledCount > 0 ? '#b00000' : 'var(--black)', whiteSpace: 'nowrap' }}>
                              {filledCount > 0
                                ? `⚠️ Permanently deletes ${posCount} position${posCount === 1 ? '' : 's'} — ${filledCount} filled with people. This cannot be undone.`
                                : `Remove this block and its ${posCount} empty position${posCount === 1 ? '' : 's'}?`}
                            </span>
                            <button className="btn btn-xs" onClick={() => setConfirmBlockId(null)}>Cancel</button>
                            <button className="btn btn-xs btn-danger" onClick={() => { setConfirmBlockId(null); onRemoveBlock(block.id); }}>
                              {filledCount > 0 ? `Delete anyway (${filledCount} people lose their spot)` : 'Remove'}
                            </button>
                          </>
                        );
                      })() : (
                        <button className="btn btn-xs btn-danger" onClick={() => setConfirmBlockId(block.id)}>
                          Remove Block
                        </button>
                      )}
                    </div>
                  </div>

                  {collapsedBlocks.has(block.id) && (
                    <div
                      style={{ padding: '8px 16px', fontFamily: 'var(--font-b)', fontSize: 12, fontWeight: 300, color: 'var(--muted)', cursor: 'pointer' }}
                      onClick={() => setCollapsedBlocks(prev => { const next = new Set(prev); next.delete(block.id); return next; })}
                      title="Click to expand"
                    >
                      {block.positionsMeta || 'No positions'} — collapsed
                    </div>
                  )}

                  {!collapsedBlocks.has(block.id) && visiblePositions.length === 0 && (
                    <div style={{ padding: '12px 16px', fontFamily: 'var(--font-b)', fontSize: 13, fontWeight: 300, color: 'var(--muted)' }}>
                      No positions{rosterFilter ? ' matching filter' : ''}. Click + Position to add one.
                    </div>
                  )}

                  {!collapsedBlocks.has(block.id) && visiblePositions.map(pos => {
                    const canDrag = !rosterFilter;
                    return (
                    <div
                      key={pos.id}
                      className="block-position-row"
                      onDragOver={canDrag ? (e) => { if (dragId != null) { e.preventDefault(); setDragOverId(pos.id); } } : undefined}
                      onDrop={canDrag ? () => handlePosDrop(block, pos.id) : undefined}
                      style={{
                        ...(dragOverId === pos.id && dragId != null && dragId !== pos.id ? { borderTop: '2px solid var(--black)' } : {}),
                        ...(dragId === pos.id ? { opacity: 0.5 } : {}),
                      }}
                    >
                      {canDrag && (
                        <span
                          draggable
                          onDragStart={() => setDragId(pos.id)}
                          onDragEnd={() => { setDragId(null); setDragOverId(null); }}
                          title="Drag to reorder"
                          style={{ cursor: 'grab', color: 'var(--subtle)', padding: '0 6px 0 0', flexShrink: 0, userSelect: 'none', lineHeight: 1 }}
                        >
                          ⠿
                        </span>
                      )}
                      <div className="block-pos-label" style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                        {editingPosId === pos.id ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                            <input
                              className="form-input"
                              style={{ fontFamily: 'var(--font-h)', fontSize: 12, fontWeight: 700, padding: '2px 6px', height: 'auto', width: 120 }}
                              value={editingPosLabel}
                              autoFocus
                              onChange={e => setEditingPosLabel(e.target.value)}
                              onKeyDown={e => { if (e.key === 'Enter') commitEditPos(pos.id); if (e.key === 'Escape') setEditingPosId(null); }}
                            />
                            <button className="btn btn-xs btn-dark" onClick={() => commitEditPos(pos.id)}>Save</button>
                            <button className="btn btn-xs" onClick={() => setEditingPosId(null)}>✕</button>
                          </div>
                        ) : (
                          <>
                            {pos.label || 'Position'}
                            <button
                              className="btn-ghost"
                              style={{ opacity: 0.35, padding: '1px 3px', color: 'var(--black)', flexShrink: 0 }}
                              title="Rename position"
                              onClick={() => startEditPos(pos)}
                            >
                              {PENCIL_ICON}
                            </button>
                          </>
                        )}
                      </div>
                      <div className="block-pos-person">
                        {pos.open ? (
                          <>
                            <span style={{ color: 'var(--subtle)', fontStyle: 'italic' }}>Open position</span>
                            {pos.declinedBy && (
                              <span style={{ fontSize: 11, color: '#900', marginLeft: 6 }}>({pos.declinedBy} declined)</span>
                            )}
                            <button
                              className="btn btn-xs btn-dark"
                              style={{ marginLeft: 10 }}
                              onClick={() => onAssign(block, pos)}
                            >
                              + Fill
                            </button>
                          </>
                        ) : (
                          <>
                            <span className={`status-dot ${
                              pos.status === 'accepted' ? 'dot-green' :
                              pos.status === 'pending'  ? 'dot-amber' : 'dot-gray'
                            }`} style={{ marginRight: 8, flexShrink: 0 }} />
                            {pos.person}
                            {pos.statusNote && (
                              <span style={{ fontSize: 11, color: 'var(--muted)', marginLeft: 8 }}>{pos.statusNote}</span>
                            )}
                          </>
                        )}
                      </div>
                      <div className="block-actions" style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                        {kidsEnabled && (
                          <label
                            title="This position serves in Kids — it will show in the Kids Conference app"
                            style={{
                              display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer',
                              fontFamily: 'var(--font-h)', fontSize: 11, fontWeight: 700,
                              letterSpacing: '0.06em', textTransform: 'uppercase',
                              color: pos.kidsFlag ? '#6AA292' : 'var(--muted)',
                              marginRight: 4, userSelect: 'none', flexShrink: 0,
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={pos.kidsFlag}
                              onChange={e => onToggleKidsFlag(pos.id, e.target.checked)}
                              style={{ accentColor: '#6AA292', flexShrink: 0, margin: 0 }}
                            />
                            Kids
                          </label>
                        )}
                        {confirmId === pos.id ? (
                          <>
                            <span style={{ fontSize: 12, fontFamily: 'var(--font-b)', fontWeight: 500, color: 'var(--black)', whiteSpace: 'nowrap' }}>
                              {pos.open ? 'Remove position?' : `Remove ${pos.person}?`}
                            </span>
                            <button className="btn btn-xs" onClick={() => setConfirmId(null)}>Cancel</button>
                            <button
                              className="btn btn-xs btn-danger"
                              onClick={() => {
                                setConfirmId(null);
                                if (pos.open) onRemoveAssignment(pos.id);
                                else onClearPerson(pos);
                              }}
                            >
                              Remove
                            </button>
                          </>
                        ) : (
                          <>
                            {!pos.open && pos.status === 'pending' && (
                              <button
                                className="btn btn-xs"
                                style={{ color: 'var(--black)', borderColor: 'var(--black)' }}
                                title="Confirm on their behalf"
                                onClick={() => onConfirmPerson(pos)}
                              >
                                Confirm
                              </button>
                            )}
                            {!pos.open && (
                              <button
                                className="btn btn-xs"
                                onClick={() => onAssign(block, pos)}
                              >
                                Change
                              </button>
                            )}
                            <button
                              className="btn-remove"
                              title={pos.open ? 'Remove position' : `Remove ${pos.person} from position`}
                              onClick={() => setConfirmId(pos.id)}
                            >
                              <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                                <path d="M2 2l8 8M10 2L2 10" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
                              </svg>
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                    );
                  })}
                </div>
              );
            })}
          </>
        )}

        <button
          className="btn"
          style={{ width: '100%', padding: 14, borderStyle: 'dashed', marginTop: enrichedBlocks.length > 0 ? 0 : undefined }}
          onClick={onAddBlock}
        >
          + Add Block
        </button>
      </div>
    </>
  );
}

// ── Sub-view: Schedule ───────────────────────────────────────────────────────

function TLScheduleView({ groupedSchedule, teamName, showToast, setAddItemOpen, mealTimesByKey = {}, onSetMealTime, onEditItem, isKids }) {
  // Leads can collapse the shared master ("all-team") items to focus on their own.
  const [hideMaster, setHideMaster] = useState(false);
  const hasMasterItems = groupedSchedule.some(g => g.items.some(i => !i._teamItem));
  // Kids filter (meeting ask): flip between the whole rundown, just the
  // kids-specific items (the team's own items + master items targeted at this
  // team), or conference-wide items that apply to everyone.
  const [schedFilter, setSchedFilter] = useState('all');
  // Block filter: show only the team's own items linked to the selected blocks.
  // Multi-select with OR semantics — pick "Thursday PM Vocal" + "Friday AM
  // Vocal" and anything linked to EITHER shows.
  const [blockFilters, setBlockFilters] = useState(() => new Set());
  const [blockDropOpen, setBlockDropOpen] = useState(false);
  const [blockQuery, setBlockQuery] = useState('');
  const linkedBlockTitles = (() => {
    const set = new Set();
    groupedSchedule.forEach(g => g.items.forEach(it => {
      if (!it._teamItem || !it.linkedBlock) return;
      String(it.linkedBlock).split('\n').forEach(t => { const s = t.trim(); if (s) set.add(s); });
    }));
    return Array.from(set).sort();
  })();
  const itemLinksToAny = (it, titleSet) => {
    if (!it._teamItem) return false;
    return String(it.linkedBlock || '').split('\n').map(s => s.trim()).some(t => titleSet.has(t));
  };
  const toggleBlockFilter = (title) => setBlockFilters(prev => {
    const next = new Set(prev);
    if (next.has(title)) next.delete(title); else next.add(title);
    return next;
  });
  const isKidsItem = (item) => {
    if (item._teamItem) return true;
    const applies = String(item.appliesToTeams || '').trim().toUpperCase();
    return applies !== '' && applies !== 'ALL';
  };
  // Apply hide-master, then the kids filter, then the block filter, then drop empty days.
  const shownGroups = groupedSchedule
    .map(group => ({ ...group, items: hideMaster ? group.items.filter(i => i._teamItem) : group.items }))
    .map(g => ({ ...g, items: (!isKids || schedFilter === 'all') ? g.items : g.items.filter(it => schedFilter === 'kids' ? isKidsItem(it) : !isKidsItem(it)) }))
    .map(g => ({ ...g, items: blockFilters.size ? g.items.filter(it => itemLinksToAny(it, blockFilters)) : g.items }))
    .filter(group => group.items.length > 0);
  // What the print modal receives: the schedule EXACTLY as filtered on
  // screen, flattened to plain rows (windowed meals show the lead's chosen
  // time when one is set, same as the on-screen view).
  const [printOpen, setPrintOpen] = useState(false);
  const printGroups = shownGroups.map(g => ({
    day: g.day,
    items: g.items.map(item => {
      const windowed = isWindowedMeal(item);
      const pick = windowed ? mealTimesByKey[mealKeyOf(item.date, item.activity)] : null;
      const chosen = pick?.chosenTime || '';
      return {
        start: windowed ? (chosen || item.mealWindowStart || item.start) : item.start,
        end: windowed ? (chosen ? '' : (item.mealWindowEnd || item.end)) : item.end,
        activity: item.activity,
        location: item.location || '',
        description: item._teamItem ? (item.description || '') : '',
        linked: item._teamItem ? String(item.linkedBlock || '').split('\n').map(s => s.trim()).filter(Boolean) : [],
      };
    }),
  }));
  return (
    <>
      <div style={{ padding: '24px 40px 20px', background: 'var(--white)', borderBottom: '1px solid var(--border)' }}>
        <div className="page-title-row" style={{ marginBottom: 0 }}>
          <div>
            <div className="page-title">Conference Schedule</div>
            <div className="page-sub">{teamName} · 2026</div>
          </div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            {linkedBlockTitles.length > 0 && (
              <div style={{ position: 'relative' }}>
                <input
                  className="form-input"
                  style={{ width: 230, padding: '7px 10px', fontSize: 13 }}
                  placeholder={blockFilters.size ? `${blockFilters.size} block${blockFilters.size === 1 ? '' : 's'} selected` : 'Filter by block…'}
                  value={blockQuery}
                  onChange={e => { setBlockQuery(e.target.value); setBlockDropOpen(true); }}
                  onFocus={() => setBlockDropOpen(true)}
                />
                {blockDropOpen && (
                  <>
                    {/* click-away backdrop */}
                    <div style={{ position: 'fixed', inset: 0, zIndex: 40 }} onClick={() => { setBlockDropOpen(false); setBlockQuery(''); }} />
                    <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 4, zIndex: 41, background: 'var(--white)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', boxShadow: '0 8px 24px rgba(0,0,0,0.13)', maxHeight: 260, overflowY: 'auto' }}>
                      {blockFilters.size > 0 && (
                        <div
                          onClick={() => setBlockFilters(new Set())}
                          style={{ padding: '8px 12px', fontFamily: 'var(--font-h)', fontSize: 12, fontWeight: 700, color: '#900', cursor: 'pointer', borderBottom: '1px solid var(--border)' }}
                        >
                          × Clear selection ({blockFilters.size})
                        </div>
                      )}
                      {linkedBlockTitles
                        .filter(t => !blockQuery.trim() || t.toLowerCase().includes(blockQuery.trim().toLowerCase()))
                        .map(t => (
                          <label
                            key={t}
                            style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid var(--border)', background: blockFilters.has(t) ? 'var(--off)' : 'var(--white)' }}
                          >
                            <input
                              type="checkbox"
                              checked={blockFilters.has(t)}
                              onChange={() => toggleBlockFilter(t)}
                              style={{ accentColor: 'var(--black)', flexShrink: 0 }}
                            />
                            <span style={{ fontFamily: 'var(--font-b)', fontSize: 13 }}>{t}</span>
                          </label>
                        ))}
                      {linkedBlockTitles.filter(t => !blockQuery.trim() || t.toLowerCase().includes(blockQuery.trim().toLowerCase())).length === 0 && (
                        <div style={{ padding: '10px 12px', fontFamily: 'var(--font-b)', fontSize: 13, color: 'var(--muted)' }}>No blocks match.</div>
                      )}
                    </div>
                  </>
                )}
              </div>
            )}
            {hasMasterItems && (
              <button
                className={`btn btn-sm${hideMaster ? ' btn-dark' : ''}`}
                onClick={() => setHideMaster(v => !v)}
                title="Show or hide the shared master schedule items"
              >
                {hideMaster ? 'Show master items' : 'Hide master items'}
              </button>
            )}
            <button className="btn btn-sm" onClick={() => setPrintOpen(true)} title="Print the team's conference schedule by day">
              🖨 Print
            </button>
            <button className="btn btn-dark" onClick={() => setAddItemOpen(true)}>+ Add Team Item</button>
          </div>
        </div>
      </div>
      <div className="page-content" style={{ paddingTop: 24 }}>
        {isKids && groupedSchedule.length > 0 && (
          <div style={{ marginBottom: 20, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontFamily: 'var(--font-h)', fontSize: 11, fontWeight: 700, letterSpacing: '0.15em', textTransform: 'uppercase', color: 'var(--muted)', marginRight: 4 }}>Show</span>
            {[
              { key: 'all', label: 'Everything' },
              { key: 'kids', label: 'Kids items only' },
              { key: 'conference', label: 'Conference-wide' },
            ].map(f => (
              <button
                key={f.key}
                className={`stat-chip${schedFilter === f.key ? ' active-chip' : ''}`}
                onClick={() => setSchedFilter(f.key)}
              >
                {f.label}
              </button>
            ))}
          </div>
        )}
        {shownGroups.length === 0 ? (
          <div className="panel" style={{ padding: '40px 24px', textAlign: 'center', color: 'var(--muted)', fontFamily: 'var(--font-b)', fontWeight: 300 }}>
            {hideMaster
              ? "No team items yet. Click \u201c+ Add Team Item\u201d to add your own, or show the master items."
              : groupedSchedule.length === 0
                ? 'No schedule items yet. The master schedule will appear here once published.'
                : 'Nothing matches this filter.'}
          </div>
        ) : shownGroups.map(group => (
          <div key={group.day}>
            <div className="section-header">{group.day}</div>
            <div className="panel" style={{ marginBottom: 24 }}>
              {group.items.map((item, ii) => {
                const tags = buildTags(item, { hideTeamTags: true });
                // A windowed meal lets the lead pick a time inside Kat's window.
                const windowed = isWindowedMeal(item);
                const winStart = item.mealWindowStart || '';
                const winEnd = item.mealWindowEnd || '';
                const pick = windowed
                  ? mealTimesByKey[mealKeyOf(item.date, item.activity)]
                  : null;
                const chosen = pick?.chosenTime || '';
                // Cream highlight until the team's time is set (V3 behavior).
                const rowBg = windowed
                  ? (chosen ? 'var(--white)' : '#fdfaf6')
                  : (item.isMeal ? '#fdfaf6' : undefined);
                return (
                  <div className="schedule-item" key={item.id || ii} style={rowBg ? { background: rowBg } : {}}>
                    <div className="schedule-time">
                      {windowed ? (
                        chosen ? formatSchedTime(chosen) : formatSchedTime(winStart || item.start)
                      ) : (
                        <>
                          {formatSchedTime(item.start)}
                          {item.end && (
                            <div style={{ fontFamily: 'var(--font-b)', fontSize: 10, fontWeight: 300, color: 'var(--muted)', marginTop: 2 }}>
                              &ndash;{formatSchedTime(item.end)}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                    <div className="schedule-detail">
                      <div className="schedule-name">
                        {item.activity}
                        {item._teamItem && item.linkedBlock && String(item.linkedBlock).split('\n').filter(Boolean).map((bl, bi) => (
                          <span
                            key={bi}
                            className="sc-pill"
                            style={{ marginLeft: 8, verticalAlign: 1, ...blockPillStyle(bl) }}
                          >
                            {bl}
                          </span>
                        ))}
                      </div>
                      <div className="schedule-loc">{item.location}</div>
                      {item._teamItem && item.description && (
                        <div style={{ fontFamily: 'var(--font-b)', fontSize: 12, fontWeight: 300, color: 'var(--mid)', marginTop: 4, whiteSpace: 'pre-wrap' }}>
                          {item.description}
                        </div>
                      )}
                      {windowed && (
                        <div style={{
                          fontFamily: 'var(--font-b)', fontSize: 11, fontWeight: 300,
                          color: chosen ? 'var(--muted)' : '#b5700a', marginTop: 4,
                        }}>
                          Window: {formatSchedTime(winStart)} &ndash; {formatSchedTime(winEnd)}
                          {chosen ? ` · At ${formatSchedTime(chosen)}` : " · Set your team's time"}
                        </div>
                      )}
                      <div className="schedule-tags">
                        {tags.map((tag, ti) => (
                          <TagPill key={ti} label={tag.label} cls={tag.cls} />
                        ))}
                      </div>
                    </div>
                    {windowed && (
                      <button
                        className="btn btn-sm"
                        onClick={() => onSetMealTime && onSetMealTime({
                          ...item, windowStart: winStart, windowEnd: winEnd, chosenTime: chosen,
                        })}
                      >
                        {chosen ? 'Edit Time' : 'Set Time'}
                      </button>
                    )}
                    {item._teamItem && (
                      <button className="btn btn-sm" onClick={() => onEditItem && onEditItem(item)}>
                        Edit
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
      <PrintScheduleModal
        open={printOpen}
        onClose={() => setPrintOpen(false)}
        groups={printGroups}
        teamName={teamName}
      />
    </>
  );
}

// ── Sub-view: Docs ────────────────────────────────────────────────────────────

// Make Google Docs/Sheets/Slides/Drive links embeddable in an iframe — Google
// blocks framing of /edit and /view URLs, so swap to /preview.
function embedUrl(url) {
  if (!url) return url;
  const u = String(url).trim();
  let m = u.match(/^(https:\/\/docs\.google\.com\/(?:document|spreadsheets|presentation)\/d\/[^/]+)/);
  if (m) return m[1] + '/preview';
  m = u.match(/^(https:\/\/drive\.google\.com\/file\/d\/[^/]+)/);
  if (m) return m[1] + '/preview';
  return u;
}

const TEAM_MANUAL_ID = 'team-manual';

function TLDocsView({ teamId, teamName, showToast, docs, onSaveDocs }) {
  const [editingDoc, setEditingDoc] = useState(null); // { label, url } or null
  const [editingManualUrl, setEditingManualUrl] = useState(null); // string or null
  const [viewingDoc, setViewingDoc] = useState(null); // doc object

  const allDocs = docs || [];
  const manualDoc = allDocs.find(d => d.id === TEAM_MANUAL_ID);
  const extraDocs = allDocs.filter(d => d.id !== TEAM_MANUAL_ID);

  function handleSaveManual() {
    if (!editingManualUrl?.trim()) { showToast('Please enter a URL.'); return; }
    const url = editingManualUrl.trim();
    const updated = manualDoc
      ? allDocs.map(d => d.id === TEAM_MANUAL_ID ? { ...d, url } : d)
      : [{ id: TEAM_MANUAL_ID, label: 'Team Manual', url }, ...allDocs];
    onSaveDocs(updated);
    showToast('Team Manual saved.');
    setEditingManualUrl(null);
  }

  function handleAdd() {
    setEditingDoc({ label: '', url: '' });
  }

  function handleSaveDoc() {
    if (!editingDoc.url.trim()) { showToast('URL is required.'); return; }
    const label = editingDoc.label.trim() || 'Document';
    const url = editingDoc.url.trim();
    const newDoc = { id: crypto.randomUUID(), label, url };
    const next = [...allDocs, newDoc];
    onSaveDocs(next);
    showToast('Document added.');
    setEditingDoc(null);
  }

  function handleRemove(id) {
    const next = allDocs.filter(d => d.id !== id);
    onSaveDocs(next);
    showToast('Document removed.');
    if (viewingDoc?.id === id) setViewingDoc(null);
  }

  if (viewingDoc) {
    return (
      <>
        <div style={{ padding: '14px 24px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 12, background: 'var(--white)', flexShrink: 0 }}>
          <button onClick={() => setViewingDoc(null)} style={{ fontFamily: 'var(--font-h)', fontSize: 12, fontWeight: 700, color: 'var(--mid)', background: 'none', border: 'none', cursor: 'pointer' }}>← Back</button>
          <span style={{ fontFamily: 'var(--font-h)', fontSize: 13, fontWeight: 700, color: 'var(--black)' }}>{viewingDoc.label}</span>
        </div>
        <div className="pdf-viewer-wrap" style={{ borderRadius: 0 }}>
          <iframe src={embedUrl(viewingDoc.url)} className="pdf-viewer-frame" style={{ height: 'calc(100vh - 113px)' }} allow="autoplay" title={viewingDoc.label} />
        </div>
      </>
    );
  }

  return (
    <>
      <div style={{ padding: '24px 40px 20px', background: 'var(--white)', borderBottom: '1px solid var(--border)' }}>
        <div className="page-title-row" style={{ marginBottom: 0 }}>
          <div>
            <div className="page-title">Team Documents</div>
            <div className="page-sub">{teamName} · visible to your team members</div>
          </div>
          <button className="btn btn-dark" onClick={handleAdd}>+ Add Document</button>
        </div>
      </div>

      <div className="page-content" style={{ paddingTop: 24, maxWidth: 560 }}>

        {/* Team Manual — always shown */}
        <div style={{ marginBottom: 28 }}>
          <div style={{ fontFamily: 'var(--font-h)', fontSize: 10, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 10 }}>Team Manual</div>
          {editingManualUrl !== null ? (
            <div className="panel" style={{ padding: 20 }}>
              <div className="form-group" style={{ marginBottom: 12 }}>
                <label className="form-label">Manual link</label>
                <input
                  className="form-input"
                  placeholder="https://docs.google.com/document/…"
                  value={editingManualUrl}
                  onChange={e => setEditingManualUrl(e.target.value)}
                  autoFocus
                />
                <div style={{ fontFamily: 'var(--font-b)', fontSize: 12, fontWeight: 300, color: 'var(--muted)', marginTop: 5 }}>Paste the full Google Doc / Slides / Drive PDF link (shared “Anyone with the link”). This is the document itself, not its title.</div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn" onClick={() => setEditingManualUrl(null)}>Cancel</button>
                <button className="btn btn-dark" onClick={handleSaveManual}>Save</button>
              </div>
            </div>
          ) : manualDoc?.url ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 0', borderBottom: '1px solid var(--border)' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontFamily: 'var(--font-h)', fontSize: 14, fontWeight: 800, color: 'var(--black)', marginBottom: 2 }}>Team Manual</div>
                <div style={{ fontFamily: 'var(--font-b)', fontSize: 12, color: 'var(--muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{manualDoc.url}</div>
              </div>
              <button className="btn btn-sm" onClick={() => setViewingDoc(manualDoc)}>Open</button>
              <button className="btn btn-sm" onClick={() => setEditingManualUrl(manualDoc.url)}>Edit</button>
              <button className="btn btn-sm btn-danger" onClick={() => handleRemove(TEAM_MANUAL_ID)}>Remove</button>
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 0', borderBottom: '1px solid var(--border)' }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontFamily: 'var(--font-h)', fontSize: 14, fontWeight: 800, color: 'var(--black)', marginBottom: 2 }}>Team Manual</div>
                <div style={{ fontFamily: 'var(--font-b)', fontSize: 12, color: 'var(--muted)' }}>No link added yet</div>
              </div>
              <button className="btn btn-sm btn-dark" onClick={() => setEditingManualUrl('')}>Add Link</button>
            </div>
          )}
        </div>

        {/* Additional documents */}
        <div>
          <div style={{ fontFamily: 'var(--font-h)', fontSize: 10, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 10 }}>Additional Documents</div>
          {editingDoc && (
            <div className="panel" style={{ padding: 24, marginBottom: 16 }}>
              <div style={{ fontFamily: 'var(--font-h)', fontSize: 13, fontWeight: 800, color: 'var(--black)', marginBottom: 16 }}>New Document</div>
              <div className="form-group">
                <label className="form-label">Name</label>
                <input className="form-input" placeholder="e.g. Day-Of Runsheet, Parking Map…" value={editingDoc.label} onChange={e => setEditingDoc(d => ({ ...d, label: e.target.value }))} />
              </div>
              <div className="form-group">
                <label className="form-label">URL</label>
                <input className="form-input" placeholder="https://drive.google.com/…" value={editingDoc.url} onChange={e => setEditingDoc(d => ({ ...d, url: e.target.value }))} />
                <div style={{ fontFamily: 'var(--font-b)', fontSize: 12, fontWeight: 300, color: 'var(--muted)', marginTop: 5 }}>Works with Google Docs, Slides, PDFs on Drive, or any URL.</div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn" onClick={() => setEditingDoc(null)}>Cancel</button>
                <button className="btn btn-dark" onClick={handleSaveDoc}>Add</button>
              </div>
            </div>
          )}
          {extraDocs.length === 0 && !editingDoc && (
            <div style={{ fontFamily: 'var(--font-b)', fontSize: 13, fontWeight: 300, color: 'var(--muted)', paddingTop: 4 }}>
              No additional documents yet. Use the button above to add runsheets, maps, or other references.
            </div>
          )}
          {extraDocs.map(doc => (
            <div key={doc.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 0', borderBottom: '1px solid var(--border)' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontFamily: 'var(--font-h)', fontSize: 14, fontWeight: 800, color: 'var(--black)', marginBottom: 2 }}>{doc.label}</div>
                <div style={{ fontFamily: 'var(--font-b)', fontSize: 12, color: 'var(--muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{doc.url}</div>
              </div>
              <button className="btn btn-sm" onClick={() => setViewingDoc(doc)}>Open</button>
              <button className="btn btn-sm btn-danger" onClick={() => handleRemove(doc.id)}>Remove</button>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

// ── Main TeamLeadView ────────────────────────────────────────────────────────

export default function TeamLeadView({
  teams, selectedTeam, setSelectedTeam,
  currentView, setCurrentView,
  openPersonPanel, showToast,
  onRolesLoaded,
  urlTeamId,
}) {
  const { user } = useAuth();
  const canSend = canUserSend(user?.email, user?.role);
  const passLevels = usePassLevels();
  // Dropdown options must cover every pass a team can confer (e.g. "Security",
  // "Leadership Co"), not just the configured defaults — otherwise a synced
  // value has no matching <option> and the select renders blank.
  const passLevelOptions = Array.from(new Set([
    ...passLevels,
    ...(teams || []).map(t => t.passLevel).filter(Boolean),
  ]));

  // Data state
  const [allPeople, setAllPeople] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [allAssignments, setAllAssignments] = useState([]);
  const [scheduleItems, setScheduleItems] = useState([]);
  // Team-created schedule items, keyed by teamId (durable, separate sheet).
  const [teamScheduleItems, setTeamScheduleItems] = useState({});
  const [teamMealTimes, setTeamMealTimes] = useState([]);
  const [teamRoles, setTeamRoles] = useState({ enabled: false, roles: [] });
  const [personRoles, setPersonRoles] = useState({});
  const [loadingTeam, setLoadingTeam] = useState(false);
  // Guards against out-of-order team loads: when switching teams fast, an older
  // team's fetch can resolve LAST and overwrite the current team's roster (the
  // "wrong team's people showing" glitch). Only the latest request may commit.
  const loadSeqRef = useRef(0);

  // Team docs state — keyed by teamId, loaded from getTeamData
  const [teamDocs, setTeamDocs] = useState({});
  // Radio flags — keyed by teamId, array of personIds needing radios
  const [radioFlags, setRadioFlags] = useState({});

  // Modal state
  const [addPersonOpen, setAddPersonOpen] = useState(false);
  const [addNewPersonOpen, setAddNewPersonOpen] = useState(false);
  const [addBlockOpen, setAddBlockOpen] = useState(false);
  const [addPositionOpen, setAddPositionOpen] = useState(false);
  const [blockTemplateOpen, setBlockTemplateOpen] = useState(false);
  const [addItemOpen, setAddItemOpen] = useState(false);
  const [editItemTarget, setEditItemTarget] = useState(null); // team schedule item being edited
  const [mealTimeTarget, setMealTimeTarget] = useState(null); // windowed meal item being set
  const [sendLinksOpen, setSendLinksOpen] = useState(false);
  const [requestAvailOpen, setRequestAvailOpen] = useState(false);
  // Serving availability for this team: { personId: { selected: ["Day|Slot"], note } }
  const [availabilityMap, setAvailabilityMap] = useState({});
  // Kids-only: most-helpful-3 responses { personId: { maxSessions, selected: [blockId] } },
  // small-group-leader map { personId: groupId }, and the group list.
  const [kidsAvailMap, setKidsAvailMap] = useState({});
  const [kidsSglMap, setKidsSglMap] = useState({});
  const [kidsGroups, setKidsGroups] = useState([]);
  const [kidsRequestOpen, setKidsRequestOpen] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);
  const [sendRequestsOpen, setSendRequestsOpen] = useState(false);
  // 'view' = the current roster's unsent items; 'team' = ALL the team's roles/
  // blocks at once (the People-tab button, so multi-role teams like Events
  // don't have to send from each role roster separately).
  const [sendScope, setSendScope] = useState('view');
  const [resendPendingOpen, setResendPendingOpen] = useState(false);
  const [printPreviewOpen, setPrintPreviewOpen] = useState(false);
  const [sendTeamEmailOpen, setSendTeamEmailOpen] = useState(false);

  // Active block/position context for modals
  const [activeBlockId, setActiveBlockId] = useState(null);
  const [activeAssignData, setActiveAssignData] = useState(null); // { block, assignment }

  // The URL names the active team — trust it over teams[0]. On a refresh,
  // selectedTeam (React state) is gone until the teams list loads; falling
  // back to teams[0] here used to render the WRONG team (e.g. Events People
  // while the URL said Kids). If the URL team can't be resolved yet, we show
  // a loading state below instead of someone else's view.
  const urlTeam = urlTeamId
    ? (selectedTeam?.id === urlTeamId ? selectedTeam : teams.find(t => t.id === urlTeamId) || null)
    : null;
  const waitingForUrlTeam = !!urlTeamId && !urlTeam;
  const team = urlTeam || selectedTeam || teams[0];
  const teamName = team?.name || 'My Team';
  const teamId = waitingForUrlTeam ? null : team?.id;
  const isKidsDept = (team?.department || '').toString().trim().toLowerCase() === 'kids';

  // Parse sub-view from currentView: "tl-<teamId>-<suffix>"
  // suffix can be: people | roster | schedule | manual | roster-{roleName}
  const prefix = `tl-${teamId}-`;
  const viewSuffix = currentView.startsWith(prefix)
    ? currentView.slice(prefix.length)
    : 'people';
  const isRoleRoster = viewSuffix.startsWith('roster-');
  const activeSubRole = isRoleRoster ? viewSuffix.slice('roster-'.length) : null;
  const subView = isRoleRoster ? 'roster' : viewSuffix;

  // Load all people once
  useEffect(() => {
    getPeople().then(d => setAllPeople(d || [])).catch(console.error);
  }, []);

  // Load team-specific data when team changes.
  // Uses getTeamData mega-endpoint: members + blocks + ALL assignments + roles in 1 request.
  // getSchedule is cached in api.js so subsequent team switches skip the network hit.
  const loadTeamData = useCallback(async (tid) => {
    if (!tid) return;
    const seq = ++loadSeqRef.current;
    setLoadingTeam(true);
    try {
      const [teamData, sched] = await Promise.all([
        getTeamData(tid),
        getSchedule(),
      ]);
      // A newer team was selected while this request was in flight — drop it.
      if (seq !== loadSeqRef.current) return;
      setTeamMembers(teamData?.members || []);
      setBlocks(teamData?.blocks || []);
      setAllAssignments(teamData?.assignments || []);
      const roles = teamData?.teamRoles || { enabled: false, roles: [] };
      setTeamRoles(roles);
      if (onRolesLoaded) onRolesLoaded(tid, roles.enabled, roles.roles);
      setPersonRoles(teamData?.personRoles || {});
      setScheduleItems(sched || []);
      setTeamScheduleItems(prev => ({ ...prev, [tid]: teamData?.teamScheduleItems || [] }));
      setTeamMealTimes(teamData?.teamMealTimes || []);
      // Load docs (multi-doc, falls back to legacy single manual)
      if (teamData?.teamDocs) {
        setTeamDocs(prev => ({ ...prev, [tid]: teamData.teamDocs }));
      } else if (teamData?.manualUrl) {
        setTeamDocs(prev => ({ ...prev, [tid]: [{ id: 'migrated-manual', label: teamData.manualLabel || 'Team Manual', url: teamData.manualUrl }] }));
      }
      // Load radio flags
      if (teamData?.radioFlags) {
        setRadioFlags(prev => ({ ...prev, [tid]: teamData.radioFlags }));
      }
      // Load serving availability (best-effort — never block team load on it)
      getServingAvailability(tid)
        .then(av => { if (seq === loadSeqRef.current) setAvailabilityMap(av || {}); })
        .catch(() => { if (seq === loadSeqRef.current) setAvailabilityMap({}); });
    } catch (err) {
      console.error('Failed to load team data:', err);
    } finally {
      if (seq === loadSeqRef.current) setLoadingTeam(false);
    }
  }, [onRolesLoaded]);

  useEffect(() => {
    loadTeamData(teamId);
  }, [teamId, loadTeamData]);

  // Kids-only data: most-helpful-3 responses, SGL links, group list. Best-effort.
  useEffect(() => {
    if (!teamId || !isKidsDept) { setKidsAvailMap({}); setKidsSglMap({}); setKidsGroups([]); return; }
    let cancelled = false; // same stale-response guard as loadTeamData above
    getKidsAvailability(teamId).then(m => { if (!cancelled) setKidsAvailMap(m || {}); }).catch(() => { if (!cancelled) setKidsAvailMap({}); });
    getKidsSGLMap().then(m => { if (!cancelled) setKidsSglMap(m || {}); }).catch(() => { if (!cancelled) setKidsSglMap({}); });
    getKidsGroups().then(g => { if (!cancelled) setKidsGroups(g || []); }).catch(() => { if (!cancelled) setKidsGroups([]); });
    return () => { cancelled = true; };
  }, [teamId, isKidsDept]);

  // Kids: how many sessions (blocks) each person is already assigned to on
  // this team — used by the Fill picker's "max services hit" gray-out.
  const kidsAssignedCounts = useMemo(() => {
    if (!isKidsDept) return {};
    const seen = {};
    const counts = {};
    allAssignments.forEach(a => {
      if (!a.personId || a.status === 'declined') return;
      const key = `${a.personId}|${a.blockId}`;
      if (seen[key]) return;
      seen[key] = true;
      counts[a.personId] = (counts[a.personId] || 0) + 1;
    });
    return counts;
  }, [isKidsDept, allAssignments]);

  async function handleSetSGL(personId, groupId) {
    const prevSgl = kidsSglMap;
    setKidsSglMap(prev => {
      const next = { ...prev };
      if (groupId) next[personId] = groupId; else delete next[personId];
      return next;
    });
    try { await setKidsSGL(personId, groupId); }
    catch (e) {
      setKidsSglMap(prevSgl); // roll back so the UI matches what's actually saved
      showToast('Save failed — try again.');
    }
  }

  // Reload the master schedule. Pass force=true to bypass the 5-min cache so the
  // master schedule (the source of truth) is always current in the team view.
  async function reloadSchedule(force) {
    try {
      if (force) invalidateCache('schedule');
      const sched = await getSchedule();
      setScheduleItems(sched || []);
    } catch (err) {
      console.error('Failed to reload schedule:', err);
    }
  }

  // Master schedule is the "bible": whenever a lead opens the Schedule tab, pull
  // the latest master fresh so admin edits/removals are reflected immediately.
  useEffect(() => {
    if (subView === 'schedule' && teamId) reloadSchedule(true);
  }, [subView, teamId]);

  // Save (create or edit) a team-created schedule item, then update local state.
  async function handleSaveTeamItem(record) {
    const saved = await upsertTeamScheduleItem({ ...record, teamId });
    setTeamScheduleItems(prev => {
      const list = prev[teamId] || [];
      const idx = list.findIndex(i => i.id === saved.id);
      const next = idx >= 0
        ? list.map(i => (i.id === saved.id ? saved : i))
        : [...list, saved];
      return { ...prev, [teamId]: next };
    });
    showToast(record.id ? 'Schedule item updated.' : 'Schedule item added.');
  }

  // Delete a team-created schedule item.
  async function handleDeleteTeamItem(id) {
    await deleteTeamScheduleItem(id, teamId);
    setTeamScheduleItems(prev => ({
      ...prev,
      [teamId]: (prev[teamId] || []).filter(i => i.id !== id),
    }));
    showToast('Schedule item deleted.');
  }

  // ── Team setup checklist (shown on every team-lead page) ───────────────────
  // Manual completions persist per team in this browser, for steps with no data
  // signal to auto-detect (walkthrough video, updating people). Auto steps are
  // data-driven (and locked). Lifted to the top level so the banner — and its
  // completed-state button — appears on all sub-views, not just People.
  const setupManualKey = `tlSetupManual:${teamId}`;
  const [setupManualDone, setSetupManualDone] = useState({});
  useEffect(() => {
    try { setSetupManualDone(JSON.parse(localStorage.getItem(`tlSetupManual:${teamId}`)) || {}); }
    catch { setSetupManualDone({}); }
  }, [teamId]);
  function toggleSetupManual(key) {
    setSetupManualDone(prev => {
      const next = { ...prev, [key]: !prev[key] };
      try { localStorage.setItem(setupManualKey, JSON.stringify(next)); } catch { /* ignore */ }
      return next;
    });
  }
  const setupRawSteps = [
    { key: 'loom', title: 'Watch the walkthrough video', sub: 'A quick Loom that walks through your whole team setup — start here', external: true },
    { key: 'people', title: 'Update your people', sub: 'Set pass levels, roles, and radio needs for your team members' },
    { key: 'blocks', title: 'Build your block schedule', sub: 'Define positions for each conference block', action: 'roster', auto: blocks.length > 0 },
    { key: 'schedule', title: 'Build out your team schedule', sub: "Add your team's own events to the schedule", action: 'schedule', auto: (teamScheduleItems[teamId] || []).length > 0 },
    { key: 'docs', title: 'Add team documents', sub: 'Share your team manual, runsheets, and other references', action: 'manual', auto: (teamDocs[teamId] || []).length > 0 },
  ];
  const setupSteps = setupRawSteps.map(s => ({
    ...s,
    locked: !!s.auto,                       // data-driven steps can't be unchecked
    done: !!s.auto || !!setupManualDone[s.key],
  }));
  function handleSetupStepClick(step) {
    if (step.external) {
      if (LOOM_WALKTHROUGH_URL) {
        window.open(LOOM_WALKTHROUGH_URL, '_blank', 'noopener,noreferrer');
        if (!setupManualDone[step.key]) toggleSetupManual(step.key); // watching checks it off
      } else {
        showToast('Walkthrough video coming soon.');
      }
      return;
    }
    setCurrentView(`tl-${teamId}-${step.action}`);
  }

  // Reload just assignments — busts cache first to guarantee fresh data
  async function reloadAssignments() {
    if (!teamId) return;
    invalidateTeamData(teamId);
    const teamData = await getTeamData(teamId);
    setAllAssignments(teamData?.assignments || []);
  }

  // Reload blocks + assignments — busts cache first
  async function reloadBlocks() {
    if (!teamId) return;
    invalidateTeamData(teamId);
    const teamData = await getTeamData(teamId);
    setBlocks(teamData?.blocks || []);
    setAllAssignments(teamData?.assignments || []);
  }

  // Reload members (after add/remove person)
  async function reloadMembers() {
    if (!teamId) return;
    const members = await getTeamMembers(teamId);
    setTeamMembers(members || []);
  }

  // ── Enriched data ──────────────────────────────────────────────────────────

  const teamPeople = teamMembers.filter(member =>
    // Hide roster rows whose person no longer exists (e.g. a deleted duplicate)
    // — they'd render as blank, nameless people. Skip the check while people
    // are still loading so the roster doesn't flash empty.
    allPeople.length === 0 || allPeople.some(p => p.id === member.personId)
  ).map(member => {
    const person = allPeople.find(p => p.id === member.personId) || {};
    const personId = person.id || member.personId;
    const personAssignments = allAssignments.filter(a => a.personId === personId && a.personId);

    let status, statusLabel;
    if (personAssignments.length === 0) {
      status = 'unplaced';
      statusLabel = 'Not yet placed';
    } else if (personAssignments.some(a => a.status === 'accepted')) {
      status = 'accepted';
      statusLabel = 'Accepted';
    } else if (personAssignments.some(a => a.status === 'pending')) {
      status = 'pending';
      statusLabel = 'Pending';
    } else {
      status = 'placed-unsent';
      statusLabel = 'Placed · not sent';
    }

    return {
      id: personId,
      name: person.name || '',
      email: person.email || '',
      phone: person.phone || '',
      brushfireId: person.brushfireId || '',
      passLevel: person.passLevel || '',
      // Registered = numeric attendee number only (placeholder text like
      // "staff" doesn't count); numeric fallback covers pre-redeploy responses.
      registered: person.registered !== undefined ? !!person.registered : /^\d+$/.test(String(person.brushfireId || '').trim()),
      bgCheck: person.bgCheck || '',
      mealsOnly: !!person.mealsOnly,
      status,
      statusLabel,
      roles: personRoles[personId] || [],
      memberId: member.id,
    };
  });

  // Filter blocks for the current roster view
  // Role roster: show blocks belonging to that role
  // Plain roster (no subRole): show blocks with no subRole — but ONLY while
  // roles are actually enabled. If a team's blocks carry a subRole from when
  // roles were on (e.g. Worship) and roles are later disabled, hiding them
  // would blank the whole roster even though everything exists on the backend.
  const teamHasRoles = !!(teamRoles?.enabled && teamRoles?.roles?.length);
  const blocksForView = activeSubRole
    ? blocks.filter(b => b.subRole === activeSubRole)
    : (teamHasRoles ? blocks.filter(b => !b.subRole || !b.subRole.trim()) : blocks);

  // Positions render in their saved manual order; unordered (legacy/new) ones
  // fall to the end in their existing order.
  const posOrder = (a) => (a.order === '' || a.order == null) ? 1e9 : Number(a.order);
  const enrichedBlocks = blocksForView.map(block => {
    const blockAssns = allAssignments.filter(a => a.blockId === block.id)
      .slice()
      .sort((a, b) => posOrder(a) - posOrder(b));
    const positions = blockAssns.map(a => {
      const person = teamPeople.find(p => p.id === a.personId);
      const statusNote = a.personId
        ? a.status === 'pending' ? '(pending)'
        : a.status === 'unsent' ? '(not sent)'
        : ''
        : '';
      return {
        id: a.id,
        label: a.positionLabel || 'Position',
        open: !a.personId,
        person: person?.name || '',
        personId: a.personId || '',
        status: a.status || 'unsent',
        statusNote,
        note: a.notes || '',
        declinedBy: a.status === 'declined' ? person?.name : null,
        kidsFlag: a.kidsFlag === true || String(a.kidsFlag || '').trim().toLowerCase() === 'true',
      };
    });
    const filled = positions.filter(p => !p.open).length;
    const open = positions.filter(p => p.open).length;
    const total = positions.length;
    const dayLabel = getDayFromDate(block.date) || block.day || '';
    const shortDate = block.date
      ? new Date(block.date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
      : '';
    const dateStr = shortDate ? `${dayLabel ? dayLabel + ', ' : ''}${shortDate}` : dayLabel;
    const timeStr = block.startTime
      ? `${formatTime(block.startTime)}${block.endTime ? '–' + formatTime(block.endTime) : ''}`
      : '';
    const positionsMeta = total > 0
      ? `${total} position${total !== 1 ? 's' : ''} · ${filled} filled · ${open} open`
      : '';
    return {
      id: block.id,
      title: block.title,
      day: block.day,
      date: block.date,
      endDate: block.endDate || '',
      startTime: block.startTime,
      endTime: block.endTime,
      meta: [dateStr, timeStr].filter(Boolean).join(' · '),
      positionsMeta,
      positions,
      colorClass: block.colorClass || '',
    };
  });

  const teamExcludesMeals = team?.excludeMeals === true || team?.excludeMeals === 'true' || team?.excludeMeals === 'TRUE';
  // Display-only: hide the master schedule's meal rows from this team's
  // schedule (lead enters their own meal times as team items). Meal COUNTS are
  // untouched — they come from serving windows, never from displayed rows.
  const teamHidesMasterMeals = team?.hideMasterMeals === true || team?.hideMasterMeals === 'true' || team?.hideMasterMeals === 'TRUE';
  const teamExcludesShuttles = team?.excludeShuttles === true || team?.excludeShuttles === 'true' || team?.excludeShuttles === 'TRUE';
  // Same transport heuristic as the backend: shuttle/transport in team or activity.
  const isTransportRow = (item) =>
    (((item.appliesToTeams || '') + ' ' + (item.team || '') + ' ' + (item.activity || '')).toLowerCase().includes('transport') ||
     ((item.appliesToTeams || '') + ' ' + (item.team || '') + ' ' + (item.activity || '')).toLowerCase().includes('shuttle'));
  // Admin-controlled per-team toggle: when on, each position gets a "Kids"
  // checkbox so leads can mark which positions serve in Kids (main -> Kids connector).
  const teamKidsEnabled = team?.kidsEnabled === true || team?.kidsEnabled === 'true' || team?.kidsEnabled === 'TRUE';
  // Group-blocks team (e.g. Worship): blocks are timeless named groups — the
  // roster hides time fields/meta, and serving times come from linked schedule items.
  const teamUsesGroupBlocks = team?.groupBlocks === true || team?.groupBlocks === 'true' || team?.groupBlocks === 'TRUE';
  // Staff/Leads team: each block carries its own meal DATE RANGE (date →
  // endDate) — everyone assigned gets every meal in that range.
  const teamIsStaffLeads = team?.staffLeads === true || team?.staffLeads === 'true' || team?.staffLeads === 'TRUE';
  // Team Schedule rules: only SL-flagged items, and only those that apply to
  // this team ("ALL" items or items tagged for the team). Meals are included
  // (when applicable) unless this team is set to exclude meals.
  const visibleScheduleItems = scheduleItems.filter(item => {
    if (isDateHeaderRow(item)) return false;       // drop day/date divider rows
    // MEALS bypass the SL? flag — the bible's conference-day meal rows
    // ("Servant Leader Lunch Window" 9/18+) aren't SL-flagged, which blanked
    // meals off every team schedule and left leads nowhere to set meal times.
    // Same rule as personal schedules: a meal that applies to the team shows.
    if (!isSL(item) && !isMealItem(item)) return false; // only SL-flagged items (meals exempt)
    if (!appliesToTeam(item, teamName)) return false; // ALL or this team
    if (item.isMeal && teamExcludesMeals) return false; // honor meal exclusion
    if (item.isMeal && teamHidesMasterMeals) return false; // looks only — lead shows their own meal items
    if (teamExcludesShuttles && !item.isMeal && isTransportRow(item)) return false; // honor shuttle exclusion
    return true;
  });
  // Merge in this team's own (durable) schedule items, flagged so the schedule
  // view can show Edit/Delete controls and the linked-block note on them.
  const ownItems = (teamScheduleItems[teamId] || []).map(it => ({ ...it, _teamItem: true, isSL: true }));
  const mergedScheduleItems = [...visibleScheduleItems, ...ownItems].sort((a, b) => {
    const da = (a.date || '').toString();
    const db = (b.date || '').toString();
    if (da !== db) return da < db ? -1 : 1;
    return (a.start || '').toString().localeCompare((b.start || '').toString());
  });
  const groupedSchedule = groupByDay(mergedScheduleItems);

  // ── Meal time slots ──────────────────────────────────────────────────────────
  // A "windowed" meal has a start AND end that differ — Kat set a window the team
  // lead picks a time within. Picks are keyed by (date + activity) because the
  // master-schedule reimport regenerates row UUIDs on every run.
  const mealTimeKey = (item) => mealKeyOf(item.date, item.activity);
  const mealTimesByKey = {};
  for (const mt of teamMealTimes) {
    mealTimesByKey[mealKeyOf(mt.date, mt.activity)] = mt;
  }

  async function handleSetMealTime(item, chosenTime) {
    const key = mealTimeKey(item);
    const rec = {
      teamId,
      date: item.date || '',
      activity: item.activity || '',
      chosenTime,
      windowStart: item.mealWindowStart || item.windowStart || '',
      windowEnd: item.mealWindowEnd || item.windowEnd || '',
    };
    // Optimistic update — dedupe with the SAME normalized key used everywhere
    // else (mealKeyOf lowercases), or "Lunch" vs "lunch" accumulates duplicates.
    const prevMealTimes = teamMealTimes;
    setTeamMealTimes(prev => {
      const next = prev.filter(mt => mealKeyOf(mt.date, mt.activity) !== key);
      next.push(rec);
      return next;
    });
    showToast('Meal time set');
    try {
      await setTeamMealTime(rec);
    } catch (err) {
      console.error('Failed to save meal time:', err);
      setTeamMealTimes(prevMealTimes); // roll back — the sheet never got it
      showToast('Failed to save meal time');
    }
  }

  // ── Send/Resend items ────────────────────────────────────────────────────────

  const unsentItems = [];
  const pendingItems = [];
  for (const block of enrichedBlocks) {
    for (const pos of block.positions) {
      if (pos.open || !pos.personId) continue;
      const personData = teamPeople.find(p => p.id === pos.personId);
      const rawAssn = allAssignments.find(a => a.id === pos.id);
      if (pos.status === 'unsent') {
        unsentItems.push({
          assignmentId: pos.id,
          blockTitle: block.title,
          positionLabel: pos.label,
          personId: pos.personId,
          personName: pos.person,
          personEmail: personData?.email || '',
          fullAssignment: rawAssn,
        });
      }
      if (pos.status === 'pending') {
        pendingItems.push({
          assignmentId: pos.id,
          blockTitle: block.title,
          positionLabel: pos.label,
          personId: pos.personId,
          personName: pos.person,
          personEmail: personData?.email || '',
          sentAt: rawAssn?.sentAt || null,
          fullAssignment: rawAssn,
        });
      }
    }
  }

  // Team-WIDE unsent items — every role/block on this team, regardless of which
  // roster view is active. Powers the People-tab "Send Requests" so multi-role
  // teams (Events) send everything in one pass.
  const teamUnsentItems = [];
  for (const a of allAssignments) {
    if (!a.personId) continue;
    const st = String(a.status || '').trim().toLowerCase();
    if (st !== '' && st !== 'unsent') continue;
    const b = blocks.find(x => x.id === a.blockId);
    if (!b) continue;
    const person = teamPeople.find(p => p.id === a.personId) || allPeople.find(p => p.id === a.personId);
    teamUnsentItems.push({
      assignmentId: a.id,
      blockTitle: b.title || 'Unnamed Block',
      positionLabel: a.positionLabel || '',
      personId: a.personId,
      personName: person?.name || '',
      personEmail: person?.email || '',
      fullAssignment: a,
    });
  }

  // ── Roster modal context ────────────────────────────────────────────────────

  const activeBlock = activeAssignData?.block;
  const activeAssignment = activeAssignData?.assignment;
  const activeBlockAssignments = activeBlock
    ? allAssignments.filter(a => a.blockId === activeBlock.id)
    : [];

  // ── Callbacks ──────────────────────────────────────────────────────────────

  // Persist a manual position order for a block (drag-to-reorder).
  async function handleReorderPositions(blockId, orderedIds) {
    // Optimistic: stamp each assignment's order to its new index.
    setAllAssignments(prev => prev.map(a => {
      if (a.blockId !== blockId) return a;
      const idx = orderedIds.indexOf(a.id);
      return idx === -1 ? a : { ...a, order: idx };
    }));
    try {
      await reorderAssignments(blockId, orderedIds);
      invalidateTeamData(teamId);
    } catch (err) {
      showToast('Could not save the new order — refreshing.');
      console.error(err);
      reloadAssignments();
    }
  }

  async function handleReorderBlocks(orderedIds) {
    // Optimistic: stamp each block's order to its new index and re-sort locally.
    const prevBlocks = blocks;
    setBlocks(prev => {
      const stamped = prev.map(b => {
        const idx = orderedIds.indexOf(b.id);
        return idx === -1 ? b : { ...b, order: idx };
      });
      const rank = (b) => (b.order === '' || b.order == null) ? 1e9 : Number(b.order);
      return stamped.slice().sort((a, b) => rank(a) - rank(b));
    });
    try {
      await reorderBlocks(teamId, orderedIds);
    } catch (err) {
      showToast('Could not save the new block order — refreshing.');
      console.error(err);
      setBlocks(prevBlocks);
    }
  }

  async function handleRemovePerson(person) {
    const prev = teamMembers;
    setTeamMembers(m => m.filter(m => m.personId !== person.id));
    try {
      await removeTeamMember(teamId, person.id);
      // Bust the cached mega-endpoint so the person stays gone on next load.
      invalidateTeamData(teamId);
      showToast(`${person.name} removed from roster.`);
    } catch (err) {
      setTeamMembers(prev);
      showToast('Error removing person.');
      console.error(err);
    }
  }

  async function handleRemoveBlock(blockId) {
    const prevBlocks = blocks;
    const prevAssignments = allAssignments;
    setBlocks(b => b.filter(b => b.id !== blockId));
    setAllAssignments(a => a.filter(a => a.blockId !== blockId));
    try {
      await deleteBlock(blockId);
      showToast('Block removed.');
    } catch (err) {
      setBlocks(prevBlocks);
      setAllAssignments(prevAssignments);
      showToast('Error removing block.');
      console.error(err);
    }
  }

  async function handleRemoveAssignment(assignmentId) {
    const assignment = allAssignments.find(a => a.id === assignmentId);
    const wasNotified = assignment?.status && assignment.status !== 'unsent';
    const prev = allAssignments;
    setAllAssignments(a => a.filter(a => a.id !== assignmentId));
    try {
      await deleteAssignment(assignmentId);
      showToast('Position removed.');
      // Auto-send change notification if this person had already been notified
      if (wasNotified && assignment?.personId && canSend) {
        const person = allPeople.find(p => p.id === assignment.personId);
        if (person?.email) {
          sendScheduleChangeEmail(person.email, person.name, person.id, user?.email).catch(console.error);
        }
      }
    } catch (err) {
      setAllAssignments(prev);
      showToast('Error removing position.');
      console.error(err);
    }
  }

  async function handleConfirmPerson(pos) {
    const rawAssn = allAssignments.find(a => a.id === pos.id);
    if (!rawAssn) return;
    const updated = { ...rawAssn, status: 'accepted', respondedAt: new Date().toISOString() };
    setAllAssignments(prev => prev.map(a => a.id === pos.id ? updated : a));
    try {
      await upsertAssignment(updated);
      showToast(`${pos.person} confirmed.`);
    } catch (err) {
      setAllAssignments(prev => prev.map(a => a.id === pos.id ? rawAssn : a));
      showToast('Error confirming — please try again.');
      console.error(err);
    }
  }

  async function handleEditBlockDetails(blockId, details) {
    const raw = blocks.find(b => b.id === blockId);
    if (!raw) return;
    const updated = { ...raw, ...details };
    setBlocks(prev => prev.map(b => b.id === blockId ? { ...b, ...details } : b));
    try {
      await upsertBlock(updated);
    } catch (err) {
      setBlocks(prev => prev.map(b => b.id === blockId ? raw : b));
      showToast('Error updating block details.');
      console.error(err);
    }
  }

  async function handleRenameBlock(blockId, title) {
    const raw = blocks.find(b => b.id === blockId);
    if (!raw) return;
    // Optimistic update — name appears instantly, API saves in background
    setBlocks(prev => prev.map(b => b.id === blockId ? { ...b, title } : b));
    try {
      await upsertBlock({ ...raw, title });
    } catch (err) {
      // Roll back on failure
      setBlocks(prev => prev.map(b => b.id === blockId ? { ...b, title: raw.title } : b));
      showToast('Error renaming block.');
      console.error(err);
    }
  }

  async function handleRenamePosition(posId, label) {
    const raw = allAssignments.find(a => a.id === posId);
    if (!raw) return;
    // Optimistic update — label appears instantly, API saves in background
    setAllAssignments(prev => prev.map(a => a.id === posId ? { ...a, positionLabel: label } : a));
    try {
      await upsertAssignment({ ...raw, positionLabel: label });
    } catch (err) {
      // Roll back on failure
      setAllAssignments(prev => prev.map(a => a.id === posId ? { ...a, positionLabel: raw.positionLabel } : a));
      showToast('Error renaming position.');
      console.error(err);
    }
  }

  async function handleToggleKidsFlag(posId, checked) {
    const raw = allAssignments.find(a => a.id === posId);
    if (!raw) return;
    // Optimistic update — checkbox flips instantly, API saves in background
    setAllAssignments(prev => prev.map(a => a.id === posId ? { ...a, kidsFlag: checked } : a));
    try {
      await upsertAssignment({ ...raw, kidsFlag: checked });
    } catch (err) {
      // Roll back on failure
      setAllAssignments(prev => prev.map(a => a.id === posId ? { ...a, kidsFlag: raw.kidsFlag } : a));
      showToast('Error saving Kids flag.');
      console.error(err);
    }
  }

  async function handleClearPerson(pos) {
    const rawAssn = allAssignments.find(a => a.id === pos.id);
    if (!rawAssn) return;
    const updated = { ...rawAssn, personId: '', status: 'unsent' };
    setAllAssignments(prev => prev.map(a => a.id === pos.id ? updated : a));
    try {
      await upsertAssignment(updated);
      showToast(`${pos.person} removed from position.`);
    } catch (err) {
      setAllAssignments(prev => prev.map(a => a.id === pos.id ? rawAssn : a));
      showToast('Error unassigning person.');
      console.error(err);
    }
  }

  async function handleSetRoles(personId, roles) {
    const prev = personRoles[personId];
    setPersonRoles(p => ({ ...p, [personId]: roles }));
    try {
      await setPersonRolesApi(teamId, personId, roles);
    } catch (err) {
      setPersonRoles(p => ({ ...p, [personId]: prev }));
      showToast('Error saving role.');
      console.error(err);
    }
  }

  async function handleSetPassLevel(personId, newLevel) {
    const person = allPeople.find(p => p.id === personId);
    if (!person) return;
    const prevLevel = person.passLevel || '';
    // Optimistically update allPeople so the change mirrors everywhere instantly
    setAllPeople(prev => prev.map(p =>
      p.id === personId ? { ...p, passLevel: newLevel, passLevelManual: true } : p
    ));
    try {
      // passLevelManual locks this value so the team auto-sync never overrides it
      await upsertPerson({ ...person, passLevel: newLevel, passLevelManual: true });
      showToast('Pass level updated');
    } catch (err) {
      setAllPeople(prev => prev.map(p =>
        p.id === personId ? { ...p, passLevel: prevLevel } : p
      ));
      showToast('Error updating pass level');
      console.error(err);
    }
  }

  function handleOpenAddPosition(blockId) {
    setActiveBlockId(blockId);
    setAddPositionOpen(true);
  }

  function handleOpenAssign(block, assignment) {
    setActiveAssignData({ block, assignment });
    setAssignOpen(true);
  }

  function handleOpenPrint() {
    setPrintPreviewOpen(true);
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  function renderSubView() {
    if (loadingTeam) {
      return (
        <div style={{ padding: '60px 40px', textAlign: 'center', color: 'var(--muted)', fontFamily: 'var(--font-b)', fontWeight: 300 }}>
          Loading team data…
        </div>
      );
    }

    switch (subView) {
      case 'roster':
        return (
          <TLRosterView
            enrichedBlocks={enrichedBlocks}
            teamName={teamName}
            activeSubRole={activeSubRole}
            showToast={showToast}
            onAddBlock={() => setAddBlockOpen(true)}
            onAddPosition={handleOpenAddPosition}
            onAssign={handleOpenAssign}
            onRemoveBlock={handleRemoveBlock}
            onRemoveAssignment={handleRemoveAssignment}
            onClearPerson={handleClearPerson}
            onRenameBlock={handleRenameBlock}
            onRenamePosition={handleRenamePosition}
            onEditBlockDetails={handleEditBlockDetails}
            onConfirmPerson={handleConfirmPerson}
            setBlockTemplateOpen={setBlockTemplateOpen}
            onSendRequests={() => { setSendScope('view'); setSendRequestsOpen(true); }}
            onResendPending={() => setResendPendingOpen(true)}
            onPrint={handleOpenPrint}
            onReorderPositions={handleReorderPositions}
            onReorderBlocks={handleReorderBlocks}
            unsentCount={unsentItems.length}
            pendingCount={pendingItems.length}
            kidsEnabled={teamKidsEnabled}
            groupBlocks={teamUsesGroupBlocks}
            staffLeads={teamIsStaffLeads}
            onToggleKidsFlag={handleToggleKidsFlag}
          />
        );
      case 'schedule':
        return (
          <TLScheduleView
            groupedSchedule={groupedSchedule}
            teamName={teamName}
            showToast={showToast}
            setAddItemOpen={setAddItemOpen}
            mealTimesByKey={mealTimesByKey}
            onSetMealTime={(item) => setMealTimeTarget(item)}
            onEditItem={(item) => { setEditItemTarget(item); setAddItemOpen(true); }}
            isKids={isKidsDept}
          />
        );
      case 'manual':
        return (
          <TLDocsView
            teamId={teamId}
            teamName={teamName}
            showToast={showToast}
            docs={teamDocs[teamId] || []}
            onSaveDocs={(docs) => {
              const prevDocs = teamDocs[teamId] || [];
              setTeamDocs(prev => ({ ...prev, [teamId]: docs }));
              saveTeamDocs(teamId, docs).catch(err => {
                console.error('Failed to save docs:', err);
                setTeamDocs(prev => ({ ...prev, [teamId]: prevDocs })); // roll back
                showToast('Saving documents failed — please try again.');
              });
            }}
          />
        );
      default:
        return (
          <TLPeopleView
            people={teamPeople}
            teamName={teamName}
            teamId={teamId}
            showToast={showToast}
            openPersonPanel={(person) => openPersonPanel({
              ...person,
              // Lets the shared PersonPanel's "Remove from Team" button actually
              // remove this person from THIS team (same handler as the row X).
              _onRemoveFromTeam: () => handleRemovePerson(person),
              // Kids availability is session-based (not the main day×time grid),
              // so the panel needs the kids answers handed to it.
              _isKids: isKidsDept,
              _kidsAvail: isKidsDept ? (kidsAvailMap[person.id] || null) : null,
              _kidsTeamId: isKidsDept ? teamId : '',
            })}
            canSend={canSend}
            senderEmail={user?.email}
            setAddPersonOpen={setAddPersonOpen}
            setSendLinksOpen={setSendLinksOpen}
            onSendRequestsAll={teamUnsentItems.length > 0 ? () => { setSendScope('team'); setSendRequestsOpen(true); } : null}
            teamUnsentCount={teamUnsentItems.length}
            setRequestAvailOpen={isKidsDept ? setKidsRequestOpen : setRequestAvailOpen}
            setSendTeamEmailOpen={setSendTeamEmailOpen}
            isKids={isKidsDept}
            kidsAvailMap={kidsAvailMap}
            kidsGroups={kidsGroups}
            kidsSglMap={kidsSglMap}
            onSetSGL={handleSetSGL}
            onRemovePerson={handleRemovePerson}
            teamRoles={teamRoles}
            personRoles={personRoles}
            onSetRole={handleSetRoles}
            passLevels={passLevelOptions}
            onSetPassLevel={handleSetPassLevel}
            radioFlags={radioFlags[teamId] || []}
            onToggleRadio={(personId) => {
              const current = radioFlags[teamId] || [];
              const flagged = !current.includes(personId);
              const next = flagged ? [...current, personId] : current.filter(id => id !== personId);
              setRadioFlags(prev => ({ ...prev, [teamId]: next }));
              setRadioFlag(teamId, personId, flagged).catch(err => {
                console.error(err);
                setRadioFlags(prev => ({ ...prev, [teamId]: current })); // roll back
                showToast('Radio flag didn’t save — please try again.');
              });
            }}
          />
        );
    }
  }

  // URL names a team we can't resolve yet (teams list still loading after a
  // refresh) — hold with a loading state rather than rendering another team.
  if (waitingForUrlTeam) {
    return (
      <div style={{ padding: '80px 40px', textAlign: 'center', color: 'var(--muted)', fontFamily: 'var(--font-b)', fontWeight: 300 }}>
        Loading team…
      </div>
    );
  }

  return (
    <>
      {teamId && (
        <SetupBanner
          teamName={teamName}
          steps={setupSteps}
          onStepClick={handleSetupStepClick}
          onToggleStep={step => toggleSetupManual(step.key)}
        />
      )}
      {renderSubView()}

      <AddPersonToRosterModal
        open={addPersonOpen}
        onClose={() => setAddPersonOpen(false)}
        teamId={teamId}
        allPeople={allPeople}
        onAdd={(member) => {
          if (member?.personId) {
            setTeamMembers(prev => [...prev, member]);
          } else {
            reloadMembers();
          }
        }}
        onAddNewPerson={() => setAddNewPersonOpen(true)}
        showToast={showToast}
      />

      <AddPersonModal
        open={addNewPersonOpen}
        onClose={() => setAddNewPersonOpen(false)}
        showToast={showToast}
        onSave={(newPerson) => {
          // Generate IDs client-side so person appears on roster instantly
          const personId = crypto.randomUUID();
          const now = new Date().toISOString();
          const personWithId = { ...newPerson, id: personId, createdAt: now };
          const memberObj = {
            id: crypto.randomUUID(),
            teamId,
            personId,
            role: 'member',
            createdAt: now,
          };
          setAllPeople(prev => [...prev, personWithId]);
          setTeamMembers(prev => [...prev, memberObj]);
          showToast(`${newPerson.name} added to roster.`);
          // Persist in background — sequential so addTeamMember sees the person exist
          upsertPerson(personWithId)
            .then(() => addTeamMember(teamId, personId, 'member'))
            .catch(err => {
              showToast('Error saving person — please refresh.');
              console.error(err);
            });
        }}
      />

      <AddBlockModal
        open={addBlockOpen}
        onClose={() => setAddBlockOpen(false)}
        teamId={teamId}
        subRole={activeSubRole}
        groupBlocks={teamUsesGroupBlocks}
        staffLeads={teamIsStaffLeads}
        showToast={showToast}
        onAdd={(newBlock, newAssignments) => {
          setBlocks(prev => [...prev, newBlock]);
          if (newAssignments?.length > 0) setAllAssignments(prev => [...prev, ...newAssignments]);
        }}
      />

      <AddPositionModal
        open={addPositionOpen}
        onClose={() => { setAddPositionOpen(false); setActiveBlockId(null); }}
        blockId={activeBlockId}
        teamId={teamId}
        showToast={showToast}
        onAdd={(newAssignments) => {
          setAllAssignments(prev => [...prev, ...newAssignments]);
        }}
      />

      <AssignPersonModal
        open={assignOpen}
        onClose={() => { setAssignOpen(false); setActiveAssignData(null); }}
        showToast={showToast}
        assignmentId={activeAssignment?.id}
        positionLabel={activeAssignment?.label}
        blockId={activeBlock?.id}
        blockTitle={activeBlock?.title}
        teamId={teamId}
        teamName={teamName}
        teamPeople={teamPeople}
        blockAssignments={activeBlockAssignments}
        activeSubRole={activeSubRole}
        availabilityMap={availabilityMap}
        kidsAvail={isKidsDept ? kidsAvailMap : null}
        kidsAssignedCounts={isKidsDept ? kidsAssignedCounts : null}
        blockDay={getDayFromDate(activeBlock?.date) || activeBlock?.day}
        blockStartTime={activeBlock?.startTime}
        currentPersonName={activeAssignment?.open ? null : activeAssignment?.person}
        onRemove={() => {
          if (activeAssignment) handleClearPerson(activeAssignment);
        }}
        onAssign={(assignment) => {
          // Update local state immediately with optimistic assignment object
          setAllAssignments(prev => prev.map(a => a.id === assignment.id ? { ...a, ...assignment } : a));
        }}
      />

      <BlockTemplateModal
        open={blockTemplateOpen}
        onClose={() => setBlockTemplateOpen(false)}
        teamId={teamId}
        subRole={activeSubRole}
        showToast={showToast}
      />

      <AddTeamScheduleItemModal
        open={addItemOpen}
        onClose={() => { setAddItemOpen(false); setEditItemTarget(null); }}
        onSave={handleSaveTeamItem}
        onDelete={handleDeleteTeamItem}
        teamName={teamName}
        blocks={blocks}
        item={editItemTarget}
        people={teamPeople}
        showToast={showToast}
      />

      <SetMealTimeModal
        open={!!mealTimeTarget}
        meal={mealTimeTarget}
        onClose={() => setMealTimeTarget(null)}
        onSave={(time) => handleSetMealTime(mealTimeTarget, time)}
        showToast={showToast}
      />

      <SendScheduleLinksModal
        open={sendLinksOpen}
        onClose={() => setSendLinksOpen(false)}
        showToast={showToast}
        teamName={teamName}
        people={teamPeople}
        senderEmail={user?.email}
        canSend={canSend}
        templateId={isKidsDept ? 'kids-schedule-link' : undefined}
      />

      <RequestAvailabilityModal
        open={requestAvailOpen}
        onClose={() => setRequestAvailOpen(false)}
        showToast={showToast}
        teamName={teamName}
        people={teamPeople}
        senderEmail={user?.email}
        canSend={canSend}
      />

      <KidsAvailabilityRequestModal
        open={kidsRequestOpen}
        onClose={() => setKidsRequestOpen(false)}
        showToast={showToast}
        senderEmail={user?.email}
        memberCount={teamPeople.length}
      />

      <SendRequestsModal
        open={sendRequestsOpen}
        onClose={() => setSendRequestsOpen(false)}
        items={sendScope === 'team' ? teamUnsentItems : unsentItems}
        teamName={teamName}
        templateId={isKidsDept ? 'kids-schedule-assignment' : undefined}
        senderEmail={user?.email}
        canSend={canSend}
        onSent={(sentIds, sentAt) => {
          // Update assignment statuses directly — no reload needed
          setAllAssignments(prev => prev.map(a =>
            sentIds.includes(a.id) ? { ...a, status: 'pending', sentAt } : a
          ));
        }}
        showToast={showToast}
      />

      <ResendPendingModal
        open={resendPendingOpen}
        onClose={() => setResendPendingOpen(false)}
        items={pendingItems}
        teamName={teamName}
        senderEmail={user?.email}
        canSend={canSend}
        onSent={() => {}} // resend doesn't change status, no state update needed
        showToast={showToast}
      />

      <PrintPreviewModal
        open={printPreviewOpen}
        onClose={() => setPrintPreviewOpen(false)}
        blocks={enrichedBlocks}
        teamName={teamName}
        teamPeople={teamPeople}
      />

      <SendTeamEmailModal
        open={sendTeamEmailOpen}
        onClose={() => setSendTeamEmailOpen(false)}
        teamId={teamId}
        members={teamPeople}
        senderEmail={user?.email}
        showToast={showToast}
      />
    </>
  );
}
